import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import type { Node } from "@xyflow/react";
import type {
    IVRBlockData,
    IVRBlockShape,
    IVRBlockConfig,
} from "./IVRSchemeBuilder";
import {
    isIvrEndBlockData,
    resolveIvrPostRouteTypeValue,
} from "./IVRSchemeBuilder";
import { IconBadge } from "./RouterMenu/IconBadge";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import type { AppDispatch } from "../../../../redux/store";
import { getAllAudio } from "../../../../redux/api/apiAudio";
import { getAudioById } from "../../../../redux/api/apiAudio";
import { checkPermissions } from "../../../../redux/permissions/permissionsUtils";
import { setEditType } from "../../../../redux/actions/actions";
import AudioDialog from "../../AudioList/AudioDialog";
import {Dialog} from "primereact/dialog";
import AudioPlayerComponent from "components/common/AudioPlayerWithSpeed";
import axios from "axios";
import {Divider} from "primereact/divider";
import { classNames } from "primereact/utils";
import { showError } from "../../../../redux/actions/actions";
import {
    removeIvrElementAudioFromStore,
    setIvrSchemeGraphApiSlice,
} from "../../../../redux/actions/actionsIVRConfiguration";
import {
    deleteIvrConfigRoute,
    getIVRConfigurationFullById,
    getIvrTempAudio,
    ivrConfigMutationRequest,
    postIvrConfigElement,
    postIvrConfigRoute,
    postIvrConfigSchedule,
    putIvrConfigConfiguration,
    putIvrConfigElement,
    putIvrConfigSchedule,
} from "../../../../redux/api/apiIVRConfiguration";
import {
    blockConfigToCreateElementPayload,
    buildIvrOnceAdvancedUpdateSchedulersPayload,
    buildIvrSchedulerDeactivateAllForElementPayload,
    buildIvrSchedulerUpdateSchedulersPayload,
    computeNextIvrEndElementId,
    extractCreatedElementIdFromResponse,
    shouldPostNewIvrConfiguratorElement,
} from "./ivrConfigElementPayload";
import {
    IVR_MENU_DTMF_KEY_ORDER,
    sortIvrMenuDtmfKeys,
} from "./ivrSchemeConstants";
import {
    apiElementDraftDataRaw,
    augmentSchedulerApiElementWithClientBlockConfig,
    buildSchedulerSlotRowsFromBlockConfig,
    IVR_BASE_NODE_ID,
    IVR_CHAIN_PREFIX,
    ivrRawScheduleActiveToId,
    ivrRawScheduleElementId,
    ivrRawSchedulePriority,
    ivrRawScheduleRowId,
    daysweekAdvancedSchedulerInvalidKeys,
    ivrSchedulerDailySlotRowsOverlap,
    ivrSchedulerSlotRowsOverlapWithinSameDaysMask,
    onceAdvancedSchedulerInvalidRowIndices,
    onceAdvancedSchedulerSlotRowsHaveCoverageGaps,
    resolveIvrSchedulesServerTruth,
    schedulerBlockConfigPatchFromSchedules,
    schedulerDayOfWeekToDaysMask,
    ivrSchedulerWeekdayChainDayOfWeek,
    ivrSchedulerSlotRowIndexFromSlotSuffix,
    schedulerSlotRowsNeedOthers,
    type IvrApiElement,
    type IvrApiRoute,
    type IvrApiSchedule,
    type IvrSchedulerSlotRow,
} from "./ivrSchemeHydration";
import {
    normalizeIvrSchedulerScheduleKind,
    parseIvrElementDraftData,
    resolveDaysweekSchedulerDaysSource,
    filterIvrSchedulerSlotRowsForScheduleApi,
    type IvrSchedulerScheduleKind,
    type SchedulerDayLike,
} from "./ivrElementDraftData";
import {
    findInactiveSchedulerSlotMatchingPostCandidate,
    nextIvrRoutePriority,
    nextIvrSchedulePostPriorityFromSources,
    slotRowsToSchedules,
} from "./ivrSchemeSerialization";

type BlockSettingsPanelProps = {
    node: Node<IVRBlockData> | null;
    resolveLatestNode?: (
        nodeId: string
    ) => Node<IVRBlockData> | null | undefined;
    getGraphElementIds?: () => number[];
    getGraphEndElementIds?: () => number[];
    resolveSchedulerJumpTargetElementId?: (
        configNodeId: string
    ) => number | null;
    flowIdFromRoute?: number | null;
    onSave?: (nodeId: string, blockConfig: IVRBlockConfig) => void;
    onGenerateChain?: (nodeId: string, config: IVRBlockConfig) => void;
    onDismissSettings?: () => void;
};

const ACCENT_COLORS: Record<IVRBlockShape, string> = {
    circle: "rgb(76, 175, 80)",
    diamond: "#5c5cff",
    square: "rgb(255, 152, 0)",
    triangle: "rgb(15, 139, 253)",
};

const DATA_TYPE_OPTIONS = [
    { id: "number", label: "Номер" },
    { id: "string", label: "Рядок" },
    { id: "time", label: "Час" },
    { id: "date", label: "Дата" },
    { id: "date_time", label: "Дата, час" },
    { id: "day_of_month", label: "День місяця" },
    { id: "day_of_week", label: "День тижня" },
    { id: "qualification_group", label: "Кваліфікаційна група" },
    { id: "queue", label: "Черга" },
];

const CONDITION_MODE_OPTIONS = [
    { value: "comparison", label: "Порівняння" },
    { value: "range", label: "Діапазон" },
];

const COMPARISON_OPERATORS = [
    { id: ">", label: "більше" },
    { id: ">=", label: "більше або дорівнює" },
    { id: "=", label: "дорівнює" },
    { id: "!=", label: "не дорівнює" },
    { id: "<", label: "менше" },
    { id: "<=", label: "менше або дорівнює" },
];

const DAY_OPTIONS = [
    { id: "monday", label: "Понеділок" },
    { id: "tuesday", label: "Вівторок" },
    { id: "wednesday", label: "Середа" },
    { id: "thursday", label: "Четвер" },
    { id: "friday", label: "П'ятниця" },
    { id: "saturday", label: "Субота" },
    { id: "sunday", label: "Неділя" },
];

const MOCK_LINES = [
    { id: "1", name: "380 97 222 22 22" },
    { id: "2", name: "380 97 222 22 23" },
    { id: "3", name: "380 97 222 22 24" },
];

const START_BLOCK_LABEL = "Start";

type StartConfigurationType =
    | "Play"
    | "Menu"
    | "Scheduler"
    | "AgentHunter"
    | "End";

const START_CONFIGURATION_OPTIONS: { id: StartConfigurationType; label: string }[] = [
    { id: "Play", label: "Play" },
    { id: "Menu", label: "Menu" },
    { id: "Scheduler", label: "Scheduler" },
    { id: "AgentHunter", label: "AgentHunter" },
    { id: "End", label: "End" },
];

const DTMF_KEYPAD_KEYS: string[] = [...IVR_MENU_DTMF_KEY_ORDER];

type SchedulerDay = {
    dayOfWeek: number;
    label: string;
    enabled: boolean;
    start: Date | null;
    end: Date | null;
};

const WEEKDAY_ABBREVS: Record<number, string> = {
    1: "ПН",
    2: "ВТ",
    3: "СР",
    4: "ЧТ",
    5: "ПТ",
    6: "СБ",
    0: "НД",
};

const DEFAULT_SCHEDULER_DAYS: SchedulerDay[] = [
    { dayOfWeek: 1, label: "Понеділок", enabled: false, start: null, end: null },
    { dayOfWeek: 2, label: "Вівторок", enabled: false, start: null, end: null },
    { dayOfWeek: 3, label: "Середа", enabled: false, start: null, end: null },
    { dayOfWeek: 4, label: "Четвер", enabled: false, start: null, end: null },
    { dayOfWeek: 5, label: "П'ятниця", enabled: false, start: null, end: null },
    { dayOfWeek: 6, label: "Субота", enabled: false, start: null, end: null },
    { dayOfWeek: 0, label: "Неділя", enabled: false, start: null, end: null },
];

function formatTimeHHMM(date: Date): string {
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
}

function parseTimeHHMM(value: string): Date {
    const [hhRaw, mmRaw] = value.split(":");
    const hh = Math.min(23, Math.max(0, parseInt(hhRaw || "0", 10) || 0));
    const mm = Math.min(59, Math.max(0, parseInt(mmRaw || "0", 10) || 0));
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    return d;
}

function defaultStartTime(): Date {
    return defaultWeekdayScheduleStart();
}

function defaultEndTime(): Date {
    return defaultWeekdayScheduleEnd();
}

function defaultWeekdayScheduleStart(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function defaultWeekdayScheduleEnd(): Date {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d;
}

function isWeekdaySchedulerKind(kind: IvrSchedulerScheduleKind): boolean {
    return kind === "daysweek" || kind === "daysweek_advanced";
}

function applyWeekdaySchedulerDefaultTimes(days: SchedulerDay[]): SchedulerDay[] {
    return days.map((d) => ({
        ...d,
        start: d.enabled ? defaultWeekdayScheduleStart() : null,
        end: d.enabled ? defaultWeekdayScheduleEnd() : null,
    }));
}

function cloneTimeHmFromDate(source: Date): Date {
    const d = new Date();
    d.setHours(source.getHours(), source.getMinutes(), 0, 0);
    return d;
}

function trimHHMMFromApiTime(t: string): string {
    const [rawHours = "0", rawMinutes = "0"] = t.trim().split(":");

    const hours = Math.min(23, Math.max(0, parseInt(rawHours, 10) || 0));
    const minutes = Math.min(59, Math.max(0, parseInt(rawMinutes, 10) || 0));

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

type SchedulerAdvancedUiRow = {
    id?: number;
    priority: number;
    start: Date | null;
    end: Date | null;
};

type DaysweekAdvancedDayUi = {
    dayOfWeek: number;
    label: string;
    enabled: boolean;
    intervals: SchedulerAdvancedUiRow[];
};

function defaultDaysweekAdvancedDays(): DaysweekAdvancedDayUi[] {
    return DEFAULT_SCHEDULER_DAYS.map((d) => ({
        dayOfWeek: d.dayOfWeek,
        label: d.label,
        enabled: false,
        intervals: [],
    }));
}

function slotRowsToDaysweekAdvancedUi(
    slotRows: IvrSchedulerSlotRow[]
): DaysweekAdvancedDayUi[] {
    const template = defaultDaysweekAdvancedDays();
    const byDow = new Map<number, IvrSchedulerSlotRow[]>();
    for (const r of slotRows) {
        const m = Number(r.daysMask) || 0;
        for (const t of template) {
            if (schedulerDayOfWeekToDaysMask(t.dayOfWeek) === m) {
                const arr = byDow.get(t.dayOfWeek) ?? [];
                arr.push(r);
                byDow.set(t.dayOfWeek, arr);
                break;
            }
        }
    }
    return template.map((t) => {
        const rows = (byDow.get(t.dayOfWeek) ?? []).sort(
            (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
        );
        if (rows.length === 0) {
            return { ...t, enabled: false, intervals: [] };
        }
        return {
            ...t,
            enabled: true,
            intervals: rows.map((r, idx) => {
                const idRaw = r.id;
                const idNum =
                    typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
                return {
                    ...(Number.isFinite(idNum) && idNum > 0 ? { id: idNum } : {}),
                    priority: Number(r.priority) || idx + 1,
                    start: r.start
                        ? parseTimeHHMM(String(r.start))
                        : defaultStartTime(),
                    end: r.end
                        ? parseTimeHHMM(String(r.end))
                        : defaultEndTime(),
                };
            }),
        };
    });
}

function buildDaysweekAdvancedSlotRowsForSave(
    days: DaysweekAdvancedDayUi[],
    activeToId: number
): IvrSchedulerSlotRow[] {
    const built: IvrSchedulerSlotRow[] = [];
    let prio = 1;
    for (const day of days) {
        if (!day.enabled) {
            continue;
        }
        const mask = schedulerDayOfWeekToDaysMask(day.dayOfWeek);
        if (!mask) {
            continue;
        }
        const valid = day.intervals.filter((r) => r.start && r.end);
        for (const r of valid) {
            const idRaw = r.id;
            const idNum =
                typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
            const base: IvrSchedulerSlotRow = {
                priority: prio++,
                daysMask: mask,
                start: trimHHMMFromApiTime(formatTimeHHMM(r.start!)),
                end: trimHHMMFromApiTime(formatTimeHHMM(r.end!)),
                activeToId,
            };
            if (Number.isFinite(idNum) && idNum > 0) {
                built.push({ ...base, id: idNum });
            } else {
                built.push(base);
            }
        }
    }
    return built;
}

function buildOnceAdvancedSlotRowsForSave(
    rows: SchedulerAdvancedUiRow[],
    existingSlotRows: IvrSchedulerSlotRow[] = []
): IvrSchedulerSlotRow[] {
    const byId = new Map<number, IvrSchedulerSlotRow>();
    for (const row of existingSlotRows) {
        const idRaw = row.id;
        const idNum =
            typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
        if (Number.isFinite(idNum) && idNum > 0) {
            byId.set(idNum, row);
        }
    }
    const valid = rows.filter((r) => r.start && r.end);
    const sorted = [...valid].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );
    return sorted.map((r, idx) => {
        const idRaw = r.id;
        const idNum =
            typeof idRaw === "number" ? idRaw : Number(idRaw ?? 0);
        const prev =
            Number.isFinite(idNum) && idNum > 0
                ? byId.get(idNum)
                : existingSlotRows[idx];
        const prevAid = Number(prev?.activeToId) || 0;
        const base: IvrSchedulerSlotRow = {
            priority: Number(r.priority) || 1,
            daysMask: 127,
            start: trimHHMMFromApiTime(formatTimeHHMM(r.start!)),
            end: trimHHMMFromApiTime(formatTimeHHMM(r.end!)),
            activeToId: prevAid > 0 ? prevAid : 0,
        };
        if (Number.isFinite(idNum) && idNum > 0) {
            return { ...base, id: idNum };
        }
        return base;
    });
}

function slotRowsFromApiSchedulesForElement(
    elementId: number,
    list: readonly IvrApiSchedule[]
): IvrSchedulerSlotRow[] {
    const rows: IvrSchedulerSlotRow[] = [];
    for (const s of list) {
        if (ivrRawScheduleElementId(s) !== elementId) {
            continue;
        }
        const idn = ivrRawScheduleRowId(s);
        if (idn <= 0) {
            continue;
        }
        const r = s as Record<string, unknown>;
        rows.push({
            id: idn,
            priority: ivrRawSchedulePriority(s) || 0,
            daysMask: Number(r.daysMask ?? r.DaysMask) || 0,
            start: trimHHMMFromApiTime(
                String(r.fromTime ?? r.FromTime ?? "")
            ),
            end: trimHHMMFromApiTime(String(r.toTime ?? r.ToTime ?? "")),
            activeToId: Number(r.activeToId ?? r.ActiveToId) || 0,
        });
    }
    rows.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    return rows;
}

function defaultDailyRangeStart(): Date {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
}

function defaultDailyRangeEnd(): Date {
    const d = new Date();
    d.setHours(21, 0, 0, 0);
    return d;
}

function isSchedulerFullDay(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return false;
    const s = formatTimeHHMM(start);
    const e = formatTimeHHMM(end);
    return s === "00:00" && (e === "23:59" || e === "00:00" || e === "24:00");
}

function complementInterval(start: Date, end: Date): { start2: Date; end2: Date } {
    const start2 = new Date(start);
    start2.setHours(end.getHours(), end.getMinutes(), 0, 0);
    const end2 = new Date(start);
    end2.setHours(start.getHours(), start.getMinutes(), 0, 0);
    return { start2, end2 };
}

function formatBlockId(nodeId: string): string {
    const match = nodeId.match(/ivr-(\d+)/);
    if (match) {
        return String(parseInt(match[1], 10)).padStart(4, "0");
    }
    return nodeId === "ivr-base" ? "0000" : nodeId;
}

const START_CONFIG_PREFIX = "start-config-";

function asMultiSelectArray<T>(value: T | T[] | null | undefined): T[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value;
    return [value as T];
}

function asNumberArrayFromConfig(raw: unknown): number[] {
    if (raw == null || raw === "") return [];
    if (Array.isArray(raw)) {
        return raw
            .map((x) => Number(x))
            .filter((n) => !Number.isNaN(n));
    }
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
}

function normalizeAudioGroupId(raw: unknown): number | null {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function groupIdFromCatalogAudioRow(row: unknown): number | null {
    if (row == null || typeof row !== "object") return null;
    const r = row as Record<string, unknown>;
    return normalizeAudioGroupId(
        r.groupId ??
            r.groupAudioId ??
            r.audioGroupId ??
            r.GroupId ??
            r.groupAudioID
    );
}

function groupIdFromAudioMetaRow(m: unknown): number | null {
    if (m == null || typeof m !== "object") return null;
    const o = m as Record<string, unknown>;
    return normalizeAudioGroupId(
        o.groupId ?? o.groupAudioId ?? o.audioGroupId ?? o.GroupId
    );
}

function findAudioGroupIdInCatalog(
    audioId: number,
    allAudioGroup: unknown[] | null | undefined
): number | null {
    const list = allAudioGroup ?? [];
    const aid = Number(audioId);
    const row = list.find((x: unknown) => {
        const r = x as { audioId?: unknown };
        return Number(r?.audioId) === aid;
    });
    return groupIdFromCatalogAudioRow(row);
}

function firstGroupIdFromAudioMeta(
    meta: unknown[] | null | undefined
): number | null {
    if (!Array.isArray(meta)) return null;
    for (const m of meta) {
        const g = groupIdFromAudioMetaRow(m);
        if (g != null) return g;
    }
    return null;
}

type AudioGroupOption = { groupId: number; groupName: string };

function mergeSelectedGroupIntoOptions(
    base: AudioGroupOption[],
    selectedGroupId: number | null,
    meta:
        | { groupId?: number | null; groupName?: string | null }[]
        | null
        | undefined
): AudioGroupOption[] {
    const gid = normalizeAudioGroupId(selectedGroupId);
    if (gid == null) return base;
    if (base.some((g) => Number(g.groupId) === gid)) return base;
    const row = (meta ?? []).find((m) => groupIdFromAudioMetaRow(m) === gid);
    const name =
        (row &&
            "groupName" in row &&
            String((row as { groupName?: string }).groupName ?? "").trim()) ||
        `Группа #${gid}`;
    return [...base, { groupId: gid, groupName: name }];
}

const SCHEDULER_SCHEDULE_OPTIONS: {
    value: IvrSchedulerScheduleKind;
    label: string;
}[] = [
    { value: "once", label: "Once" },
    { value: "once_advanced", label: "Once (advanced)" },
    { value: "daysweek", label: "DaysWeek" },
    { value: "daysweek_advanced", label: "DaysWeek (advanced)" },
];

function normalizeStartConfiguration(
    raw: unknown
): StartConfigurationType | null {
    const v = String(raw ?? "")
        .trim()
        .toUpperCase();
    if (!v) return null;
    if (v === "PLAY") return "Play";
    if (v === "MENU") return "Menu";
    if (v === "SCHEDULER" || v === "SCHEDULE") return "Scheduler";
    if (
        v === "AGENTHUNTER" ||
        v === "AGENT_HUNTER" ||
        v === "HUNTER" ||
        v === "OPERATOR"
    ) {
        return "AgentHunter";
    }
    return null;
}

type IvrBlockFieldError = string | { name: string };

type IvrBlockErrorFields = {
    playAudios?: IvrBlockFieldError;
    menuAudios?: IvrBlockFieldError;
    hunterQueue?: IvrBlockFieldError;
    schedulerSlots?: IvrBlockFieldError;
};

function ivrBlockFieldErrorMessage(err: IvrBlockFieldError | undefined): string {
    if (err == null) return "";
    if (typeof err === "string") return err;
    return err.name ?? "";
}

function countValidIvrAudioIds(ids: number[] | undefined): number {
    return (ids ?? []).filter(
        (id) => id != null && Number.isFinite(Number(id)) && Number(id) > 0
    ).length;
}

function audioNameFromCatalogOrMetaRow(row: unknown): string | null {
    if (row == null || typeof row !== "object") return null;
    const r = row as Record<string, unknown>;
    const candidates = [
        r.audioName,
        r.AudioName,
        r.name,
        r.Name,
        r.fileName,
        r.FileName,
    ];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return null;
}

function audioDisplayNameFromIvrAudioRow(row: unknown, fallbackAudioId: number): string {
    return (
        audioNameFromCatalogOrMetaRow(row) || `Audio #${fallbackAudioId}`
    );
}

const IVR_SELECTED_AUDIO_LIST_LABEL_MAX = 26;

function formatSelectedAudioListLabel(fullName: string): string {
    const s = fullName.trim();
    if (s.length <= IVR_SELECTED_AUDIO_LIST_LABEL_MAX) return s;
    const head = s.slice(0, IVR_SELECTED_AUDIO_LIST_LABEL_MAX).trimEnd();
    return head.length > 0 ? `${head}...` : `…`;
}

function ivrMultiSelectValueToAudioId(value: unknown): number | null {
    if (value == null) return null;
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        return value;
    }
    if (typeof value === "string") {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    if (typeof value === "object" && value !== null && "audioId" in value) {
        const n = Number((value as { audioId: unknown }).audioId);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
}

const BlockSettingsPanel = ({
    node,
    resolveLatestNode,
    getGraphElementIds,
    getGraphEndElementIds,
    resolveSchedulerJumpTargetElementId,
    flowIdFromRoute,
    onSave,
    onGenerateChain,
    onDismissSettings,
}: BlockSettingsPanelProps) => {
    const dispatch = useAppDispatch();
    const jwtToken = useAppSelector((state) => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(
        (state) => state.RolesReducer.rolesVision
    ) as unknown as Record<string, string[]>;
    const ivrConfigurationPermissions = useMemo(
        () => checkPermissions(rolesVision, "IVRConfig"),
        [rolesVision]
    );
    const hasIVRConfigurationCreatePermission = !!ivrConfigurationPermissions.create;
    const hasIVRConfigurationUpdatePermission = !!ivrConfigurationPermissions.update;
    const canMutateIVRScheme =
        hasIVRConfigurationCreatePermission || hasIVRConfigurationUpdatePermission;
    const isReadOnlyIVRScheme = !canMutateIVRScheme;
    // @ts-ignore
    const allAudioGroup = useAppSelector((state) => state.QueueListReducer.allAudioGroup);
    const allQueues = useAppSelector((state) => state.QueueListReducer.allQueues);
    const detailsLoading = useAppSelector((state) => state.AudioListReducer.detailsLoading);
    const ivrFull = useAppSelector(
        (state) => state.IVRConfigurationReducer.ivrConfigurationData
    );

    const themeColorScheme = useAppSelector((state) => state.ThemeReducer.colorScheme);
    const isEasterEggPink =
        typeof window !== "undefined" &&
        localStorage.getItem("easterEggPink") === "true";
    const ivrPanelLightScheme = themeColorScheme === "light" || isEasterEggPink;

    const ivrDtmfKeypadContainerStyle = useMemo<React.CSSProperties>(
        () => ({
            ...(ivrPanelLightScheme
                ? {
                      background: "#e8ecf4",
                      border: "1px solid rgba(36, 40, 60, 0.12)",
                      boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 1px 4px rgba(31, 35, 55, 0.08)",
                  }
                : {
                      background: "#363946",
                  }),
            gap: "6px",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
        }),
        [ivrPanelLightScheme]
    );

    const ivrDtmfKeyButtonStyle = useCallback(
        (selected: boolean): React.CSSProperties =>
            ivrPanelLightScheme
                ? {
                      background: selected
                          ? "var(--primary-color, #0f8bfd)"
                          : "#ffffff",
                      color: selected ? "#ffffff" : "#3f455c",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderColor: selected
                          ? "var(--primary-color)"
                          : "rgba(36, 40, 60, 0.2)",
                      minHeight: "40px",
                  }
                : {
                      background: selected
                          ? "var(--primary-color, #6366f1)"
                          : "#464954",
                      color: "#ffffff",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderColor: selected
                          ? "var(--primary-color)"
                          : "transparent",
                      minHeight: "40px",
                  },
        [ivrPanelLightScheme]
    );

    const [savingIvrElement, setSavingIvrElement] = useState(false);
    const [ivrBlockErrorFields, setIvrBlockErrorFields] =
        useState<IvrBlockErrorFields | null>(null);
    const playAudiosFieldRef = useRef<HTMLDivElement | null>(null);
    const menuAudiosFieldRef = useRef<HTMLDivElement | null>(null);
    const hunterQueueFieldRef = useRef<HTMLDivElement | null>(null);
    const [selectedLines, setSelectedLines] = useState<
        { id: string; name: string }[]
    >([]);
    const [dataType, setDataType] = useState<string>("");
    const [selectedDay, setSelectedDay] = useState<string>("");
    const [conditionMode, setConditionMode] = useState<string>("comparison");
    const [comparisonOperator, setComparisonOperator] = useState<string>("=");
    const [conditionValue, setConditionValue] = useState<number | null>(null);

    const [startConfiguration, setStartConfiguration] =
        useState<StartConfigurationType>("Play");
    const [startConfigTitle, setStartConfigTitle] = useState<string>("");
    const [selectedAudioGroupId, setSelectedAudioGroupId] = useState<number | null>(null);
    const [selectedPlayAudioIds, setSelectedPlayAudioIds] = useState<number[]>([]);
    const [playSilensPre, setPlaySilensPre] = useState<number | null>(null);
    const [playSilensPost, setPlaySilensPost] = useState<number | null>(null);
    const [playTimeout, setPlayTimeout] = useState<number | null>(null);
    const [schedulerDays, setSchedulerDays] = useState<SchedulerDay[]>(DEFAULT_SCHEDULER_DAYS);
    const [schedulerScheduleKind, setSchedulerScheduleKind] =
        useState<IvrSchedulerScheduleKind>("daysweek");
    const [schedulerExtraStart, setSchedulerExtraStart] = useState<Date | null>(null);
    const [schedulerExtraEnd, setSchedulerExtraEnd] = useState<Date | null>(null);
    const [schedulerAdvancedRows, setSchedulerAdvancedRows] = useState<
        SchedulerAdvancedUiRow[]
    >([]);
    const [schedulerAdvancedInvalidRows, setSchedulerAdvancedInvalidRows] =
        useState<Set<number>>(() => new Set());
    const [schedulerDaysweekAdvancedDays, setSchedulerDaysweekAdvancedDays] =
        useState<DaysweekAdvancedDayUi[]>(defaultDaysweekAdvancedDays);
    const [schedulerDaysweekAdvancedInvalidKeys, setSchedulerDaysweekAdvancedInvalidKeys] =
        useState<Set<string>>(() => new Set());
    const [schedulerActiveToId, setSchedulerActiveToId] = useState<number | null>(null);
    const [hunterQueueIds, setHunterQueueIds] = useState<number[]>([]);

    const [menuAudioGroupId, setMenuAudioGroupId] = useState<number | null>(null);
    const [menuAudioIds, setMenuAudioIds] = useState<number[]>([]);
    const [menuSilensPre, setMenuSilensPre] = useState<number | null>(null);
    const [menuSilensPost, setMenuSilensPost] = useState<number | null>(null);
    const [menuTimeout, setMenuTimeout] = useState<number | null>(null);
    const [menuTimeoutRetries, setMenuTimeoutRetries] = useState<number | null>(null);
    const [menuRetries, setMenuRetries] = useState<number | null>(null);
    const [menuDtmfKeys, setMenuDtmfKeys] = useState<string[]>([]);
    const [menuDtmfRetries, setMenuDtmfRetries] = useState<
        Record<string, number | null>
    >({});
    const savedMenuDtmfKeysRef = useRef<string[]>([]);
    const savedSchedulerSlotRowsRef = useRef<IvrSchedulerSlotRow[]>([]);

    const dialerPermissions = useMemo(
        () => checkPermissions(rolesVision, "Audio"),
        [rolesVision]
    );
    const {
        visible: hasAudioVisiblePermission,
        update: hasAudioUpdatePermission,
    } = dialerPermissions;

    const removeIvrAudioFromSavedElement = useCallback(
        async (audioId: number): Promise<boolean> => {
            if (!node) return true;
            const sourceNode = resolveLatestNode?.(node.id) ?? node;
            const bc = sourceNode?.data?.blockConfig as
                | Record<string, unknown>
                | undefined;
            const rawEl = bc?.elementId;
            const elId =
                typeof rawEl === "number" ? rawEl : Number(rawEl);
            const flowRaw = (
                ivrFull?.configuration as { flowId?: number | null } | undefined
            )?.flowId;
            const flowIdNum = flowRaw != null ? Number(flowRaw) : NaN;
            if (
                !Number.isFinite(elId) ||
                elId <= 0 ||
                !Number.isFinite(flowIdNum) ||
                flowIdNum <= 0
            ) {
                return true;
            }
            return dispatch(removeIvrElementAudioFromStore(elId, audioId));
        },
        [node, resolveLatestNode, ivrFull, dispatch]
    );

    const selectedPlayAudios = useMemo(() => {
        const list = allAudioGroup ?? [];
        const byId = new Map<number, any>(
            list.map((x: any) => [x.audioId as number, x])
        );
        const meta =
            (node?.data.blockConfig?.playAudioMeta as
                | { audioId: number; audioName: string; groupId?: number | null; groupName?: string }[]
                | undefined) ?? [];
        return selectedPlayAudioIds.map((id) => {
            const row = byId.get(id);
            if (row) {
                const audioName = audioDisplayNameFromIvrAudioRow(row, id);
                return { ...row, audioId: id, audioName };
            }
            const m = meta.find((x) => x.audioId === id);
            if (m) {
                return {
                    audioId: m.audioId,
                    audioName: audioDisplayNameFromIvrAudioRow(m, id),
                    groupId: m.groupId ?? null,
                    groupName: m.groupName,
                };
            }
            return { audioId: id, audioName: `Audio #${id}` };
        });
    }, [allAudioGroup, selectedPlayAudioIds, node?.data.blockConfig?.playAudioMeta]);

    const currentGroupAudioIds = useMemo(() => {
        const gid = normalizeAudioGroupId(selectedAudioGroupId);
        if (gid == null) return [];
        return (allAudioGroup ?? [])
            .filter((item: unknown) => groupIdFromCatalogAudioRow(item) === gid)
            .map((item: any) => Number(item.audioId))
            .filter((id: number) => !Number.isNaN(id));
    }, [allAudioGroup, selectedAudioGroupId]);

    const selectedPlayAudioIdsInCurrentGroup = useMemo(() => {
        if (normalizeAudioGroupId(selectedAudioGroupId) == null) return [];
        const allowed = new Set(currentGroupAudioIds);
        return selectedPlayAudioIds.filter((id) => allowed.has(id));
    }, [currentGroupAudioIds, selectedAudioGroupId, selectedPlayAudioIds]);

    const selectedMenuAudios = useMemo(() => {
        const list = allAudioGroup ?? [];
        const byId = new Map<number, any>(list.map((x: any) => [x.audioId as number, x]));
        const meta =
            (node?.data.blockConfig?.menuAudioMeta as
                | { audioId: number; audioName: string; groupId?: number | null; groupName?: string }[]
                | undefined) ?? [];
        return menuAudioIds.map((id) => {
            const row = byId.get(id);
            if (row) {
                const audioName = audioDisplayNameFromIvrAudioRow(row, id);
                return { ...row, audioId: id, audioName };
            }
            const m = meta.find((x) => x.audioId === id);
            if (m) {
                return {
                    audioId: m.audioId,
                    audioName: audioDisplayNameFromIvrAudioRow(m, id),
                    groupId: m.groupId ?? null,
                    groupName: m.groupName,
                };
            }
            return { audioId: id, audioName: `Audio #${id}` };
        });
    }, [allAudioGroup, menuAudioIds, node?.data.blockConfig?.menuAudioMeta]);

    const menuCurrentGroupAudioIds = useMemo(() => {
        const gid = normalizeAudioGroupId(menuAudioGroupId);
        if (gid == null) return [];
        return (allAudioGroup ?? [])
            .filter((item: unknown) => groupIdFromCatalogAudioRow(item) === gid)
            .map((item: any) => Number(item.audioId))
            .filter((id: number) => !Number.isNaN(id));
    }, [allAudioGroup, menuAudioGroupId]);

    const menuAudioIdsInCurrentGroup = useMemo(() => {
        if (normalizeAudioGroupId(menuAudioGroupId) == null) return [];
        const allowed = new Set(menuCurrentGroupAudioIds);
        return menuAudioIds.filter((id) => allowed.has(id));
    }, [menuCurrentGroupAudioIds, menuAudioGroupId, menuAudioIds]);

    const menuAudioMultiSelectOptions = useMemo(() => {
        const list = allAudioGroup ?? [];
        const menuGid = normalizeAudioGroupId(menuAudioGroupId);
        if (menuGid == null) return [];
        return list.filter(
            (item: unknown) => groupIdFromCatalogAudioRow(item) === menuGid
        );
    }, [allAudioGroup, menuAudioGroupId]);

    const menuAudioIdsForMultiSelect = useMemo(() => {
        if (normalizeAudioGroupId(menuAudioGroupId) == null) return [];
        const allowed = new Set(menuCurrentGroupAudioIds);
        return menuAudioIds.filter((id) => allowed.has(id));
    }, [menuAudioGroupId, menuCurrentGroupAudioIds, menuAudioIds]);

    const selectedPlayAudioMultiSelectOptions = useMemo(() => {
        const list = allAudioGroup ?? [];
        const playGid = normalizeAudioGroupId(selectedAudioGroupId);
        if (playGid == null) return [];
        return list.filter(
            (item: unknown) => groupIdFromCatalogAudioRow(item) === playGid
        );
    }, [allAudioGroup, selectedAudioGroupId]);

    const selectedPlayAudioIdsForMultiSelect = useMemo(() => {
        if (normalizeAudioGroupId(selectedAudioGroupId) == null) return [];
        const allowed = new Set(currentGroupAudioIds);
        return selectedPlayAudioIds.filter((id) => allowed.has(id));
    }, [currentGroupAudioIds, selectedAudioGroupId, selectedPlayAudioIds]);

    const playIvrAudioSelectedItemTemplate = useCallback(
        (item: unknown) => {
            const id = ivrMultiSelectValueToAudioId(item);
            if (id == null) return null;
            const row = selectedPlayAudios.find((x) => x.audioId === id);
            const full = (row?.audioName ?? "").trim() || `Audio #${id}`;
            const visible = formatSelectedAudioListLabel(full);
            return (
                <span className="ivr-audio-multiselect-chip-label" title={full}>
                    {visible}
                </span>
            );
        },
        [selectedPlayAudios]
    );

    const menuIvrAudioSelectedItemTemplate = useCallback(
        (item: unknown) => {
            const id = ivrMultiSelectValueToAudioId(item);
            if (id == null) return null;
            const row = selectedMenuAudios.find((x) => x.audioId === id);
            const full = (row?.audioName ?? "").trim() || `Audio #${id}`;
            const visible = formatSelectedAudioListLabel(full);
            return (
                <span className="ivr-audio-multiselect-chip-label" title={full}>
                    {visible}
                </span>
            );
        },
        [selectedMenuAudios]
    );

    const [audioFieldLoadingKey, setAudioFieldLoadingKey] = useState<string | null>(null);

    const [ivrAudioDialogVisible, setIvrAudioDialogVisible] = useState(false);
    const [ivrAudioDialogUrl, setIvrAudioDialogUrl] = useState<string | null>(null);
    const [ivrAudioDialogName, setIvrAudioDialogName] = useState<string>("");
    const [ivrAudioLoading, setIvrAudioLoading] = useState<string | null>(null);
    const ivrAudioPlayerRef = useRef<any>(null);

    const handlePlayIvrAudio = useCallback(
        async (audioId: number, audioName: string, key: string) => {
            setIvrAudioLoading(key);
            try {
                const result = await getIvrTempAudio(audioId, jwtToken?.jwtToken);
                if (result) {
                    setIvrAudioDialogUrl(result.tempUrl);
                    setIvrAudioDialogName(
                        result.fileName || audioName || `Audio #${audioId}`
                    );
                    setIvrAudioDialogVisible(true);
                } else {
                    dispatch(showError());
                }
            } catch {
                dispatch(showError());
            } finally {
                setIvrAudioLoading(null);
            }
        },
        [jwtToken?.jwtToken, dispatch]
    );

    const handleCloseIvrAudioDialog = useCallback(() => {
        setIvrAudioDialogVisible(false);
        setIvrAudioDialogUrl(null);
        setIvrAudioDialogName("");
    }, []);

    const handleDownloadIvrAudio = useCallback(async () => {
        if (!ivrAudioDialogUrl) return;
        const resp = await axios.get(ivrAudioDialogUrl, {
            responseType: "blob",
            headers: { cache: "no-store", "Cache-Control": "no-cache" },
        });
        const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", ivrAudioDialogName || "audio.mp3");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, [ivrAudioDialogUrl, ivrAudioDialogName]);

    useEffect(() => {
        dispatch(getAllAudio(jwtToken?.jwtToken));
    }, [dispatch, jwtToken?.jwtToken]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.playAudios || startConfiguration !== "Play") {
            return;
        }
        playAudiosFieldRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [ivrBlockErrorFields?.playAudios, startConfiguration]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.menuAudios || startConfiguration !== "Menu") {
            return;
        }
        menuAudiosFieldRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [ivrBlockErrorFields?.menuAudios, startConfiguration]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.hunterQueue || startConfiguration !== "AgentHunter") {
            return;
        }
        hunterQueueFieldRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [ivrBlockErrorFields?.hunterQueue, startConfiguration]);

    useEffect(() => {
        if (schedulerScheduleKind !== "once_advanced") return;
        setSchedulerAdvancedRows((prev) => {
            if (prev.length > 0) return prev;
            return [
                {
                    priority: 1,
                    start: defaultStartTime(),
                    end: defaultEndTime(),
                },
            ];
        });
    }, [schedulerScheduleKind]);

    useEffect(() => {
        if (schedulerScheduleKind !== "once_advanced") {
            setSchedulerAdvancedInvalidRows(new Set());
            return;
        }
        const nextInvalid =
            onceAdvancedSchedulerInvalidRowIndices(schedulerAdvancedRows);
        setSchedulerAdvancedInvalidRows(nextInvalid);

        const validBuilt = buildOnceAdvancedSlotRowsForSave(
            schedulerAdvancedRows,
            []
        );
        const hasCoverageGaps =
            validBuilt.length > 0 &&
            onceAdvancedSchedulerSlotRowsHaveCoverageGaps(validBuilt);

        if (
            nextInvalid.size === 0 &&
            !hasCoverageGaps &&
            ivrBlockErrorFields?.schedulerSlots
        ) {
            setIvrBlockErrorFields((prev) => {
                if (!prev?.schedulerSlots) return prev;
                const { schedulerSlots: _removed, ...rest } = prev;
                return Object.keys(rest).length ? rest : null;
            });
        }
    }, [
        schedulerAdvancedRows,
        schedulerScheduleKind,
        ivrBlockErrorFields?.schedulerSlots,
    ]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.playAudios) return;
        if (countValidIvrAudioIds(selectedPlayAudioIds) >= 1) {
            setIvrBlockErrorFields((prev) => {
                if (!prev?.playAudios) return prev;
                const { playAudios: _removed, ...rest } = prev;
                return Object.keys(rest).length ? rest : null;
            });
        }
    }, [selectedPlayAudioIds, ivrBlockErrorFields?.playAudios]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.menuAudios) return;
        if (countValidIvrAudioIds(menuAudioIds) >= 1) {
            setIvrBlockErrorFields((prev) => {
                if (!prev?.menuAudios) return prev;
                const { menuAudios: _removed, ...rest } = prev;
                return Object.keys(rest).length ? rest : null;
            });
        }
    }, [menuAudioIds, ivrBlockErrorFields?.menuAudios]);

    useEffect(() => {
        if (!ivrBlockErrorFields?.hunterQueue) return;
        if (countValidIvrAudioIds(hunterQueueIds) >= 1) {
            setIvrBlockErrorFields((prev) => {
                if (!prev?.hunterQueue) return prev;
                const { hunterQueue: _removed, ...rest } = prev;
                return Object.keys(rest).length ? rest : null;
            });
        }
    }, [hunterQueueIds, ivrBlockErrorFields?.hunterQueue]);

    useEffect(() => {
        if (!node) {
            setSelectedLines([]);
            setDataType("");
            setSelectedDay("");
            setConditionMode("comparison");
            setComparisonOperator("=");
            setConditionValue(null);
            setStartConfiguration("Play");
            setSelectedAudioGroupId(null);
            setSelectedPlayAudioIds([]);
            handleCloseIvrAudioDialog();
            setPlaySilensPre(null);
            setPlaySilensPost(null);
            setPlayTimeout(null);
            setSchedulerDays(DEFAULT_SCHEDULER_DAYS);
            setSchedulerScheduleKind("daysweek");
            setSchedulerDaysweekAdvancedDays(defaultDaysweekAdvancedDays());
            setSchedulerDaysweekAdvancedInvalidKeys(new Set());
            setSchedulerExtraStart(null);
            setSchedulerExtraEnd(null);
            setSchedulerActiveToId(null);
            setHunterQueueIds([]);
            setMenuAudioGroupId(null);
            setMenuAudioIds([]);
            setMenuSilensPre(null);
            setMenuSilensPost(null);
            setMenuTimeout(null);
            setMenuTimeoutRetries(null);
            setMenuRetries(null);
            setMenuDtmfKeys([]);
            setMenuDtmfRetries({});
            savedMenuDtmfKeysRef.current = [];
            setStartConfigTitle("");
            setIvrBlockErrorFields(null);
            return;
        }
        const cfg = node.data.blockConfig;
        setIvrBlockErrorFields(null);
        setDataType(cfg?.dataType ?? "");
        setSelectedDay(cfg?.selectedDay ?? "");
        setConditionMode((cfg?.conditionMode as string) ?? "comparison");
        setComparisonOperator((cfg?.comparisonOperator as string) ?? "=");
        setConditionValue((cfg?.conditionValue as number) ?? null);
        setSelectedLines([]);

        const startConfRaw = normalizeStartConfiguration(
            cfg?.startConfiguration
        );
        const elementTypeRaw = String(cfg?.elementType ?? "")
            .trim()
            .toUpperCase();
        const inferredStartConf = normalizeStartConfiguration(elementTypeRaw);
        const startConf: StartConfigurationType =
            startConfRaw ?? inferredStartConf ?? "Play";
        setStartConfiguration(startConf);
        setStartConfigTitle(
            String(cfg?.startConfigTitle ?? cfg?.name ?? "").trim()
        );

        const catalogList = allAudioGroup ?? [];
        const playIds = asNumberArrayFromConfig(cfg?.playAudioIds);
        let playGid = normalizeAudioGroupId(cfg?.playAudioGroupId);
        if (
            startConf === "Play" &&
            playGid == null &&
            playIds.length > 0 &&
            catalogList.length > 0
        ) {
            playGid =
                firstGroupIdFromAudioMeta(
                    cfg?.playAudioMeta as unknown[] | undefined
                ) ??
                findAudioGroupIdInCatalog(
                    playIds[playIds.length - 1],
                    catalogList
                );
        }
        setSelectedPlayAudioIds(playIds);
        setSelectedAudioGroupId(playGid);

        setPlaySilensPre((cfg?.playSilensPre as number | null) ?? null);
        setPlaySilensPost((cfg?.playSilensPost as number | null) ?? null);
        setPlayTimeout((cfg?.playTimeout as number | null) ?? null);
        const rawElementId = cfg?.elementId;
        const elementIdFromConfig =
            typeof rawElementId === "number"
                ? rawElementId
                : Number(rawElementId);
        const elementIdFromNodeIdMatch = /^ivr-(\d+)$/.exec(String(node.id));
        const elementIdFromNodeId = elementIdFromNodeIdMatch
            ? Number(elementIdFromNodeIdMatch[1])
            : NaN;
        const elementIdNum =
            Number.isFinite(elementIdFromConfig) && elementIdFromConfig > 0
                ? elementIdFromConfig
                : elementIdFromNodeId;

        const cfgRecord = cfg as Record<string, unknown> | undefined;
        const draftParsedFromBlock = parseIvrElementDraftData(
            cfgRecord?.draftData ?? cfgRecord?.DraftData
        );
        const kindFromBlockDraft =
            typeof draftParsedFromBlock?.schedulerScheduleKind === "string"
                ? draftParsedFromBlock.schedulerScheduleKind
                : typeof (
                      draftParsedFromBlock as Record<string, unknown> | null
                  )?.SchedulerScheduleKind === "string"
                  ? String(
                        (draftParsedFromBlock as Record<string, unknown>)
                            .SchedulerScheduleKind
                    )
                  : undefined;

        let kindFromApiDraft: string | undefined;
        let apiDraftSchedulerDays: {
            dayOfWeek: number;
            enabled?: boolean;
            start?: string;
            end?: string;
        }[] = [];
        if (
            startConf === "Scheduler" &&
            Number.isFinite(elementIdNum) &&
            elementIdNum > 0
        ) {
            const apiEl = (
                (ivrFull?.elements ?? []) as IvrApiElement[]
            ).find((e) => Number(e.elementId) === elementIdNum);
            if (apiEl) {
                const d = parseIvrElementDraftData(
                    apiElementDraftDataRaw(apiEl)
                );
                if (typeof d?.schedulerScheduleKind === "string") {
                    kindFromApiDraft = d.schedulerScheduleKind;
                }
                if (d != null) {
                    const draftDaysRaw =
                        (Array.isArray(d.schedulerDays)
                            ? d.schedulerDays
                            : null) ??
                        (Array.isArray(
                            (d as Record<string, unknown>).SchedulerDays
                        )
                            ? (d as Record<string, unknown>).SchedulerDays
                            : null);
                    if (Array.isArray(draftDaysRaw)) {
                        apiDraftSchedulerDays =
                            draftDaysRaw as typeof apiDraftSchedulerDays;
                    }
                }
            }
        }

        const kindRaw =
            kindFromBlockDraft ??
            kindFromApiDraft ??
            (cfg?.schedulerScheduleKind as string | undefined);
        const normalizedSchedKind =
            normalizeIvrSchedulerScheduleKind(kindRaw);

        const schedulerFallbackPatch =
            Number.isFinite(elementIdNum) && elementIdNum > 0
                ? schedulerBlockConfigPatchFromSchedules(
                      elementIdNum,
                      resolveIvrSchedulesServerTruth(ivrFull),
                      ((ivrFull?.routes ?? []) as IvrApiRoute[]),
                      normalizedSchedKind
                  )
                : null;
        const cfgDays =
            (cfg?.schedulerDays as
                | { dayOfWeek: number; enabled: boolean; start?: string; end?: string }[]
                | undefined) ?? [];
        const fallbackSchedulerDays = schedulerFallbackPatch?.schedulerDays ?? [];

        const extra = cfg?.schedulerExtraWindow as
            | { start?: string; end?: string; start2?: string; end2?: string }
            | undefined;

        setSchedulerScheduleKind(normalizedSchedKind);

        if (normalizedSchedKind === "daysweek") {
            const daysweekSource: SchedulerDayLike[] =
                resolveDaysweekSchedulerDaysSource(
                    cfgDays,
                    apiDraftSchedulerDays,
                    fallbackSchedulerDays
                );
            setSchedulerDays((prev) =>
                prev.map((d) => {
                    const found = daysweekSource.find(
                        (x) => x.dayOfWeek === d.dayOfWeek
                    );
                    if (!found) return d;
                    return {
                        ...d,
                        enabled: !!found.enabled,
                        start: found.start ? parseTimeHHMM(found.start) : null,
                        end: found.end ? parseTimeHHMM(found.end) : null,
                    };
                })
            );
        } else {
            setSchedulerDays(
                DEFAULT_SCHEDULER_DAYS.map((d) => ({ ...d }))
            );
        }

        if (extra?.start && extra?.end) {
            setSchedulerExtraStart(parseTimeHHMM(extra.start));
            setSchedulerExtraEnd(parseTimeHHMM(extra.end));
        } else {
            setSchedulerExtraStart(null);
            setSchedulerExtraEnd(null);
        }

        const slotRowsFromCfg =
            (cfg?.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
        const fallbackSlotRows = schedulerFallbackPatch?.schedulerSlotRows ?? [];
        const slotRowsSource =
            startConf === "Scheduler" &&
            slotRowsFromCfg.length === 0 &&
            fallbackSlotRows.length > 0
                ? fallbackSlotRows
                : slotRowsFromCfg.length > 0
                ? slotRowsFromCfg
                : fallbackSlotRows;
        const rawSchedTarget =
            (cfg?.schedulerActiveToId as number | undefined) ??
            slotRowsSource[0]?.activeToId;
        const schedAid =
            typeof rawSchedTarget === "number"
                ? rawSchedTarget
                : Number(rawSchedTarget);
        setSchedulerActiveToId(
            Number.isFinite(schedAid) && schedAid > 0 ? schedAid : null
        );

        savedSchedulerSlotRowsRef.current = slotRowsSource;

        if (normalizedSchedKind === "daysweek_advanced") {
            setSchedulerDaysweekAdvancedDays(
                slotRowsToDaysweekAdvancedUi(slotRowsSource)
            );
            setSchedulerAdvancedRows([]);
        } else if (normalizedSchedKind === "once_advanced") {
            setSchedulerDaysweekAdvancedDays(defaultDaysweekAdvancedDays());
            const mask127 = slotRowsSource.filter(
                (r) => (Number(r.daysMask) || 0) === 127
            );
            if (mask127.length > 0) {
                setSchedulerAdvancedRows(
                    [...mask127]
                        .sort(
                            (a, b) =>
                                (a.priority ?? 0) - (b.priority ?? 0)
                        )
                        .map((r) => {
                            const idRaw = r.id;
                            const idNum =
                                typeof idRaw === "number"
                                    ? idRaw
                                    : Number(idRaw ?? 0);
                            return {
                                ...(Number.isFinite(idNum) && idNum > 0
                                    ? { id: idNum }
                                    : {}),
                                priority: Number(r.priority) || 1,
                                start: r.start
                                    ? parseTimeHHMM(String(r.start))
                                    : defaultStartTime(),
                                end: r.end
                                    ? parseTimeHHMM(String(r.end))
                                    : defaultEndTime(),
                            };
                        })
                );
            } else {
                setSchedulerAdvancedRows([
                    {
                        priority: 1,
                        start: defaultStartTime(),
                        end: defaultEndTime(),
                    },
                ]);
            }
        } else {
            setSchedulerAdvancedRows([]);
            setSchedulerDaysweekAdvancedDays(defaultDaysweekAdvancedDays());
        }

        const cfgHunterQueues = (cfg?.hunterQueueIds as number[] | undefined) ?? [];
        setHunterQueueIds(Array.isArray(cfgHunterQueues) ? cfgHunterQueues : []);

        const menuIds = asNumberArrayFromConfig(cfg?.menuAudioIds);
        let menuGid = normalizeAudioGroupId(cfg?.menuAudioGroupId);
        if (
            startConf === "Menu" &&
            menuGid == null &&
            menuIds.length > 0 &&
            catalogList.length > 0
        ) {
            menuGid =
                firstGroupIdFromAudioMeta(
                    cfg?.menuAudioMeta as unknown[] | undefined
                ) ??
                findAudioGroupIdInCatalog(
                    menuIds[menuIds.length - 1],
                    catalogList
                );
        }
        setMenuAudioIds(menuIds);
        setMenuAudioGroupId(menuGid);

        setMenuSilensPre((cfg?.menuSilensPre as number | null) ?? null);
        setMenuSilensPost((cfg?.menuSilensPost as number | null) ?? null);
        setMenuTimeout((cfg?.menuTimeout as number | null) ?? null);
        setMenuTimeoutRetries(
            (cfg?.menuTimeoutRetries as number | null) ?? null
        );
        setMenuRetries((cfg?.menuRetries as number | null) ?? null);
        const dtmf = (cfg?.menuDtmfKeys as string[] | undefined);
        const dtmfArr = sortIvrMenuDtmfKeys(Array.isArray(dtmf) ? dtmf : []);
        setMenuDtmfKeys(dtmfArr);
        savedMenuDtmfKeysRef.current = dtmfArr;
        const dtmfRetriesRaw =
            (cfg?.menuDtmfRetries as Record<string, number | null> | undefined) ??
            undefined;
        setMenuDtmfRetries(
            dtmfRetriesRaw && typeof dtmfRetriesRaw === "object"
                ? dtmfRetriesRaw
                : {}
        );
    }, [node?.id, node?.data.blockConfig, allAudioGroup, ivrFull]);

    const uniqueAudioGroups = useMemo((): AudioGroupOption[] => {
        const list = (allAudioGroup ?? []) as Record<string, unknown>[];
        const pairs: AudioGroupOption[] = [];
        for (const item of list) {
            const gid = groupIdFromCatalogAudioRow(item);
            if (gid == null) continue;
            const rawName =
                item.groupName ??
                item.audioGroupName ??
                item.groupAudioName ??
                "";
            const name = String(rawName).trim() || `Группа #${gid}`;
            pairs.push({ groupId: gid, groupName: name });
        }
        return Array.from(
            new Map(pairs.map((opt) => [opt.groupId, opt])).values()
        );
    }, [allAudioGroup]);

    const playAudioGroupDropdownOptions = useMemo(
        () =>
            mergeSelectedGroupIntoOptions(
                uniqueAudioGroups,
                selectedAudioGroupId,
                (node?.data.blockConfig?.playAudioMeta as
                    | { groupId?: number | null; groupName?: string | null }[]
                    | undefined) ?? []
            ),
        [
            uniqueAudioGroups,
            selectedAudioGroupId,
            node?.data.blockConfig?.playAudioMeta,
        ]
    );

    const menuAudioGroupDropdownOptions = useMemo(
        () =>
            mergeSelectedGroupIntoOptions(
                uniqueAudioGroups,
                menuAudioGroupId,
                (node?.data.blockConfig?.menuAudioMeta as
                    | { groupId?: number | null; groupName?: string | null }[]
                    | undefined) ?? []
            ),
        [
            uniqueAudioGroups,
            menuAudioGroupId,
            node?.data.blockConfig?.menuAudioMeta,
        ]
    );

    const schedulerDailyComplement = useMemo(() => {
        if (!schedulerExtraStart || !schedulerExtraEnd) return null;
        if (isSchedulerFullDay(schedulerExtraStart, schedulerExtraEnd))
            return null;
        return complementInterval(schedulerExtraStart, schedulerExtraEnd);
    }, [schedulerExtraStart, schedulerExtraEnd]);

    const handleSchedulerScheduleKindChange = useCallback(
        (nextRaw: unknown) => {
            const nextKind = normalizeIvrSchedulerScheduleKind(nextRaw);
            const prevKind = schedulerScheduleKind;
            setSchedulerScheduleKind(nextKind);

            const enteringWeekdayKind =
                isWeekdaySchedulerKind(nextKind) &&
                !isWeekdaySchedulerKind(prevKind);
            const switchingBetweenWeekdayKinds =
                isWeekdaySchedulerKind(prevKind) &&
                isWeekdaySchedulerKind(nextKind) &&
                prevKind !== nextKind;

            if (enteringWeekdayKind) {
                setSchedulerDays(
                    DEFAULT_SCHEDULER_DAYS.map((d) => ({ ...d }))
                );
                setSchedulerDaysweekAdvancedDays(defaultDaysweekAdvancedDays());
                setSchedulerAdvancedRows([]);
                setSchedulerDaysweekAdvancedInvalidKeys(new Set());
            } else if (switchingBetweenWeekdayKinds) {
                setSchedulerDays((prev) =>
                    applyWeekdaySchedulerDefaultTimes(prev)
                );
                setSchedulerDaysweekAdvancedDays(defaultDaysweekAdvancedDays());
                setSchedulerDaysweekAdvancedInvalidKeys(new Set());
            }
        },
        [schedulerScheduleKind]
    );

    useEffect(() => {
        if (startConfiguration !== "Scheduler" || schedulerScheduleKind !== "once")
            return;
        if (schedulerExtraStart != null || schedulerExtraEnd != null) return;
        setSchedulerExtraStart(defaultDailyRangeStart());
        setSchedulerExtraEnd(defaultDailyRangeEnd());
    }, [
        startConfiguration,
        schedulerScheduleKind,
        schedulerExtraStart,
        schedulerExtraEnd,
    ]);

    const latestNodeForElement = resolveLatestNode?.(node?.id ?? "") ?? node;
    const rawElForSaved = latestNodeForElement?.data?.blockConfig?.elementId;
    const elNumForSaved =
        typeof rawElForSaved === "number"
            ? rawElForSaved
            : Number(String(rawElForSaved ?? "").trim());
    const isElementSaved =
        Number.isFinite(elNumForSaved) && !Number.isNaN(elNumForSaved) && elNumForSaved >= 0;

    const savedAudioIds = useMemo(() => {
        if (!isElementSaved) return new Set<number>();
        const elements = (ivrFull?.elements ?? []) as IvrApiElement[];
        const el = elements.find((e) => e.elementId === elNumForSaved);
        if (!el?.audios) return new Set<number>();
        return new Set(el.audios.map((a) => a.audioId));
    }, [isElementSaved, elNumForSaved, ivrFull?.elements]);

    if (!node) {
        return (
            <div className="ivr-block-settings-empty p-4 text-center text-color-secondary">
                Оберіть блок на схемі для налаштування
            </div>
        );
    }

    const { data } = node;
    const accentColor = data.accentColor || ACCENT_COLORS[data.shape];
    const startBaseIconColor = "rgb(211 193 1)";
    const endBlockIconColor = "rgb(255 0 0)";
    const displayId = formatBlockId(node.id);
    const isGateway = data.shape === "diamond";
    const isEvent = data.shape === "circle";
    const isStartConfig = node.id.startsWith(START_CONFIG_PREFIX);
    const isStartBase = node.id === "ivr-base";
    const iconBadgeAccentColor = isIvrEndBlockData(data)
        ? endBlockIconColor
        : isStartBase
          ? startBaseIconColor
          : accentColor;

    const latestNodeForScheduler = resolveLatestNode?.(node.id) ?? node;
    const schedulerElRaw = latestNodeForScheduler?.data?.blockConfig?.elementId;
    const schedulerElNum =
        typeof schedulerElRaw === "number"
            ? schedulerElRaw
            : Number(schedulerElRaw);
    const hideSchedulerActiveToIdField =
        startConfiguration === "Scheduler" &&
        (!Number.isFinite(schedulerElNum) || schedulerElNum < 0);

    const onPlayAudioRowReorder = (e: { dragIndex: number; dropIndex: number }) => {
        setSelectedPlayAudioIds((prev) => {
            const updated = [...prev];
            const dragged = updated[e.dragIndex];
            if (dragged == null) return prev;
            updated.splice(e.dragIndex, 1);
            updated.splice(e.dropIndex, 0, dragged);
            return updated;
        });
    };

    const onMenuAudioRowReorder = (e: { dragIndex: number; dropIndex: number }) => {
        setMenuAudioIds((prev) => {
            const updated = [...prev];
            const dragged = updated[e.dragIndex];
            if (dragged == null) return prev;
            updated.splice(e.dragIndex, 1);
            updated.splice(e.dropIndex, 0, dragged);
            return updated;
        });
    };

    const handleSave = async () => {
        if (!canMutateIVRScheme) {
            return;
        }
        const blockConfig: IVRBlockConfig = {};
        if (isEvent && !isStartBase && !isStartConfig) {
            blockConfig.lineIds = selectedLines.map((l) => l.id);
        }
        if (isGateway) {
            blockConfig.dataType = dataType;
            if (dataType === "day_of_week") {
                blockConfig.selectedDay = selectedDay;
            }
            if (dataType === "number") {
                blockConfig.conditionMode = conditionMode;
                blockConfig.comparisonOperator = comparisonOperator;
                blockConfig.conditionValue = conditionValue;
            }
        }
        if (isStartBase) {
            return;
        }

        if (isStartConfig) {
            const sourceNode =
                resolveLatestNode?.(node.id) ?? node;
            const prevCfg = (sourceNode?.data.blockConfig ?? {}) as IVRBlockConfig;
            Object.assign(blockConfig, prevCfg);
            blockConfig.startConfiguration = startConfiguration;
            blockConfig.startConfigTitle = startConfigTitle;
            blockConfig.configSourceNodeId =
                sourceNode?.data.configSourceNodeId;
            const apiTypeByConf: Record<StartConfigurationType, string> = {
                Play: "PLAY",
                Menu: "MENU",
                Scheduler: "SCHEDULER",
                AgentHunter: "OPERATOR",
                End: "END",
            };
            blockConfig.elementType = apiTypeByConf[startConfiguration];
            if (startConfiguration === "Play") {
                blockConfig.playAudioGroupId =
                    normalizeAudioGroupId(selectedAudioGroupId);
                blockConfig.playAudioIds = selectedPlayAudioIds;
                blockConfig.playSilensPre = playSilensPre;
                blockConfig.playSilensPost = playSilensPost;
                blockConfig.playTimeout = playTimeout;

                const byId = new Map<number, any>(
                    (allAudioGroup ?? []).map((x: any) => [x.audioId as number, x])
                );
                const existingPlayMeta =
                    (prevCfg?.playAudioMeta as
                        | { audioId: number; audioName: string; groupId?: number | null; groupName?: string }[]
                        | undefined) ?? [];
                blockConfig.playAudioMeta = selectedPlayAudioIds
                    .map((id) => {
                        const item = byId.get(id);
                        if (item) {
                            return {
                                audioId: item.audioId,
                                audioName: item.audioName,
                                groupId: item.groupId,
                                groupName: item.groupName,
                            };
                        }
                        const m = existingPlayMeta.find((x) => x.audioId === id);
                        if (m) {
                            return {
                                audioId: m.audioId,
                                audioName: m.audioName,
                                groupId: m.groupId ?? null,
                                groupName: m.groupName,
                            };
                        }
                        return { audioId: id, audioName: `Audio #${id}` };
                    });
            } else {
                blockConfig.playAudioGroupId = null;
                blockConfig.playAudioIds = [];
                blockConfig.playSilensPre = null;
                blockConfig.playSilensPost = null;
                blockConfig.playTimeout = null;
                blockConfig.playAudioMeta = [];
            }
            if (startConfiguration === "Menu") {
                blockConfig.menuAudioGroupId =
                    normalizeAudioGroupId(menuAudioGroupId);
                blockConfig.menuAudioIds = menuAudioIds;
                blockConfig.menuSilensPre = menuSilensPre;
                blockConfig.menuSilensPost = menuSilensPost;
                blockConfig.menuTimeout = menuTimeout;
                blockConfig.menuTimeoutRetries = menuTimeoutRetries;
                blockConfig.menuRetries = menuRetries;
                blockConfig.menuDtmfKeys = menuDtmfKeys;
                blockConfig.menuDtmfRetries = menuDtmfRetries;
                const byId = new Map<number, any>(
                    (allAudioGroup ?? []).map((x: any) => [x.audioId as number, x])
                );
                const existingMenuMeta =
                    (prevCfg?.menuAudioMeta as
                        | { audioId: number; audioName: string; groupId?: number | null; groupName?: string }[]
                        | undefined) ?? [];
                blockConfig.menuAudioMeta = menuAudioIds
                    .map((id) => {
                        const item = byId.get(id);
                        if (item) {
                            return {
                                audioId: item.audioId,
                                audioName: item.audioName,
                                groupId: item.groupId,
                                groupName: item.groupName,
                            };
                        }
                        const m = existingMenuMeta.find((x) => x.audioId === id);
                        if (m) {
                            return {
                                audioId: m.audioId,
                                audioName: m.audioName,
                                groupId: m.groupId ?? null,
                                groupName: m.groupName,
                            };
                        }
                        return { audioId: id, audioName: `Audio #${id}` };
                    });
            } else {
                blockConfig.menuAudioGroupId = null;
                blockConfig.menuAudioIds = [];
                blockConfig.menuSilensPre = null;
                blockConfig.menuSilensPost = null;
                blockConfig.menuTimeout = null;
                blockConfig.menuTimeoutRetries = null;
                blockConfig.menuRetries = null;
                blockConfig.menuDtmfKeys = [];
                blockConfig.menuDtmfRetries = {};
                blockConfig.menuAudioMeta = [];
            }
            if (startConfiguration === "Scheduler") {
                blockConfig.schedulerScheduleKind =
                    normalizeIvrSchedulerScheduleKind(schedulerScheduleKind);
                if (schedulerScheduleKind === "daysweek") {
                    blockConfig.schedulerDays = schedulerDays.map((d) => ({
                        dayOfWeek: d.dayOfWeek,
                        label: d.label,
                        enabled: d.enabled,
                        start: d.start ? formatTimeHHMM(d.start) : "",
                        end: d.end ? formatTimeHHMM(d.end) : "",
                    }));
                    blockConfig.schedulerExtraWindow = undefined;
                } else if (
                    schedulerScheduleKind === "once_advanced" ||
                    schedulerScheduleKind === "daysweek_advanced"
                ) {
                    blockConfig.schedulerDays = [];
                    blockConfig.schedulerExtraWindow = undefined;
                } else {
                    blockConfig.schedulerDays = [];
                    if (schedulerExtraStart && schedulerExtraEnd) {
                        const full = isSchedulerFullDay(
                            schedulerExtraStart,
                            schedulerExtraEnd
                        );
                        const extraPartial = full
                            ? {}
                            : (() => {
                                  const c = complementInterval(
                                      schedulerExtraStart,
                                      schedulerExtraEnd
                                  );
                                  return {
                                      start2: formatTimeHHMM(c.start2),
                                      end2: formatTimeHHMM(c.end2),
                                  };
                              })();
                        blockConfig.schedulerExtraWindow = {
                            start: formatTimeHHMM(schedulerExtraStart),
                            end: formatTimeHHMM(schedulerExtraEnd),
                            ...extraPartial,
                        };
                    } else {
                        blockConfig.schedulerExtraWindow = undefined;
                    }
                }
            } else {
                blockConfig.schedulerDays = [];
                blockConfig.schedulerExtraWindow = undefined;
                blockConfig.schedulerScheduleKind = undefined;
            }
            if (startConfiguration === "AgentHunter") {
                blockConfig.hunterQueueIds = hunterQueueIds;
                const queuesById = new Map<number, any>(
                    (allQueues ?? []).map((q: any) => [q.id as number, q])
                );
                blockConfig.hunterQueueMeta = hunterQueueIds
                    .map((id) => {
                        const q = queuesById.get(id);
                        if (!q) return null;
                        return {
                            queueId: q.id,
                            queueName: q.name,
                        };
                    })
                    .filter(Boolean);
            } else {
                blockConfig.hunterQueueIds = [];
                blockConfig.hunterQueueMeta = [];
            }
            if (startConfiguration === "End") {
                const prev = prevCfg as Record<string, unknown>;
                blockConfig.elementType = "END";
                blockConfig.name =
                    String(prev?.name ?? "END").trim() || "END";
                blockConfig.startConfigTitle = blockConfig.name;
                blockConfig.flowId = prev?.flowId;
                blockConfig.elementId = prev?.elementId;
                blockConfig.audios = prev?.audios ?? null;
                blockConfig.elementData = prev?.elementData ?? null;
                blockConfig.minDigits = (prev?.minDigits as number | null) ?? null;
                blockConfig.maxDigits = (prev?.maxDigits as number | null) ?? null;
                blockConfig.terminators = prev?.terminators ?? null;
                blockConfig.regexp = (prev?.regexp as string | null) ?? null;
                blockConfig.digitTimeOut =
                    (prev?.digitTimeOut as number | null) ?? null;
                blockConfig.timeOut = (prev?.timeOut as number | null) ?? null;
                blockConfig.silencePre = (prev?.silencePre as number | null) ?? null;
                blockConfig.silencePost =
                    (prev?.silencePost as number | null) ?? null;
            }

            if (startConfiguration === "Scheduler") {
                const prevSchedKind = normalizeIvrSchedulerScheduleKind(
                    (prevCfg as Record<string, unknown>)?.schedulerScheduleKind
                );
                const nextSchedKind = normalizeIvrSchedulerScheduleKind(
                    schedulerScheduleKind
                );
                const schedulerKindChanged = prevSchedKind !== nextSchedKind;

                let schedTargetId = NaN;
                if (!schedulerKindChanged && schedulerActiveToId != null) {
                    const v =
                        typeof schedulerActiveToId === "number"
                            ? schedulerActiveToId
                            : Number(schedulerActiveToId);
                    if (Number.isFinite(v)) schedTargetId = v;
                }

                if (
                    !schedulerKindChanged &&
                    (!Number.isFinite(schedTargetId) || schedTargetId < 0)
                ) {
                    const fromGraph =
                        resolveSchedulerJumpTargetElementId?.(sourceNode.id) ??
                        null;
                    if (
                        fromGraph != null &&
                        Number.isFinite(fromGraph) &&
                        fromGraph >= 0
                    ) {
                        schedTargetId = fromGraph;
                    }
                }

                if (
                    !schedulerKindChanged &&
                    (!Number.isFinite(schedTargetId) || schedTargetId < 0)
                ) {
                    const rows = (blockConfig.schedulerSlotRows as
                        | { activeToId?: number }[]
                        | undefined) ?? [];
                    for (const row of rows) {
                        const aid = row?.activeToId;
                        const n =
                            typeof aid === "number" ? aid : Number(aid);
                        if (Number.isFinite(n) && n >= 0) {
                            schedTargetId = n;
                            break;
                        }
                    }
                }

                if (!Number.isFinite(schedTargetId) || schedTargetId < 0) {
                    schedTargetId = 0;
                }
                blockConfig.schedulerActiveToId = schedulerKindChanged
                    ? 0
                    : schedTargetId;

                if (schedulerScheduleKind === "once_advanced") {
                    const invalidAdvRows =
                        onceAdvancedSchedulerInvalidRowIndices(
                            schedulerAdvancedRows
                        );
                    const prevAdvSlotRows =
                        (blockConfig.schedulerSlotRows as
                            | IvrSchedulerSlotRow[]
                            | undefined) ?? [];
                    const advBuilt = buildOnceAdvancedSlotRowsForSave(
                        schedulerAdvancedRows,
                        schedulerKindChanged ? [] : prevAdvSlotRows
                    );
                    const hasOverlap =
                        advBuilt.length > 0 &&
                        ivrSchedulerDailySlotRowsOverlap(advBuilt);
                    const hasCoverageGaps =
                        advBuilt.length > 0 &&
                        onceAdvancedSchedulerSlotRowsHaveCoverageGaps(
                            advBuilt
                        );
                    if (
                        advBuilt.length === 0 ||
                        invalidAdvRows.size > 0 ||
                        hasOverlap ||
                        hasCoverageGaps
                    ) {
                        setSchedulerAdvancedInvalidRows(
                            new Set(invalidAdvRows)
                        );
                        setIvrBlockErrorFields((prev) => ({
                            ...(prev ?? {}),
                            schedulerSlots: {
                                name:
                                    advBuilt.length === 0
                                        ? "Додайте хоча б один часовий інтервал із заповненими полями З / По."
                                        : hasCoverageGaps
                                          ? "Інтервали мають покривати добу без пропусків. Додайте або змініть інтервали."
                                          : "Перевірте виділені інтервали: заповніть час або усуньте перетин і дублікати.",
                            },
                        }));
                        return;
                    }
                    setSchedulerAdvancedInvalidRows(new Set());
                    delete (blockConfig as Record<string, unknown>).schedulerShowOthers;
                    blockConfig.schedulerSlotRows = advBuilt;
                    blockConfig.schedulerShowOthers = false;
                } else if (schedulerScheduleKind === "daysweek_advanced") {
                    const invalidKeys =
                        daysweekAdvancedSchedulerInvalidKeys(
                            schedulerDaysweekAdvancedDays
                        );
                    const dwBuilt = buildDaysweekAdvancedSlotRowsForSave(
                        schedulerDaysweekAdvancedDays,
                        0
                    );
                    const hasOverlap =
                        dwBuilt.length > 0 &&
                        ivrSchedulerSlotRowsOverlapWithinSameDaysMask(dwBuilt);
                    if (
                        dwBuilt.length === 0 ||
                        invalidKeys.size > 0 ||
                        hasOverlap
                    ) {
                        setSchedulerDaysweekAdvancedInvalidKeys(
                            new Set(invalidKeys)
                        );
                        setIvrBlockErrorFields((prev) => ({
                            ...(prev ?? {}),
                            schedulerSlots: {
                                name:
                                    dwBuilt.length === 0
                                        ? "Увімкніть хоча б один день і додайте інтервал із заповненими полями З / По."
                                        : "Перевірте виділені інтервали: заповніть час або усуньте перетин і дублікати в межах дня.",
                            },
                        }));
                        return;
                    }
                    setSchedulerDaysweekAdvancedInvalidKeys(new Set());
                    delete (blockConfig as Record<string, unknown>).schedulerShowOthers;
                    blockConfig.schedulerSlotRows = dwBuilt;
                    blockConfig.schedulerShowOthers =
                        schedulerSlotRowsNeedOthers(dwBuilt);
                } else {
                    delete (blockConfig as Record<string, unknown>).schedulerSlotRows;
                    delete (blockConfig as Record<string, unknown>).schedulerShowOthers;
                    const schedRows = buildSchedulerSlotRowsFromBlockConfig(
                        blockConfig as Record<string, unknown>,
                        schedTargetId
                    );
                    blockConfig.schedulerSlotRows = schedRows;
                }
            }

            if (startConfiguration === "Play") {
                if (countValidIvrAudioIds(selectedPlayAudioIds) < 1) {
                    setIvrBlockErrorFields((prev) => ({
                        ...(prev ?? {}),
                        playAudios: {
                            name: "Для конфігурації Play потрібно додати хоча б одну аудіозапис.",
                        },
                    }));
                    return;
                }
            }
            if (startConfiguration === "Menu") {
                if (countValidIvrAudioIds(menuAudioIds) < 1) {
                    setIvrBlockErrorFields((prev) => ({
                        ...(prev ?? {}),
                        menuAudios: {
                            name: "Для конфігурації Menu потрібно додати хоча б одну аудіозапис.",
                        },
                    }));
                    return;
                }
            }
            if (startConfiguration === "AgentHunter") {
                if (countValidIvrAudioIds(hunterQueueIds) < 1) {
                    setIvrBlockErrorFields((prev) => ({
                        ...(prev ?? {}),
                        hunterQueue: {
                            name: "Потрібно обрати чергу*",
                        },
                    }));
                    return;
                }
            }

            const flowRaw = ivrFull?.configuration?.flowId;
            let flowIdNum = flowRaw != null ? Number(flowRaw) : NaN;
            if (!Number.isFinite(flowIdNum) || flowIdNum <= 0) {
                const fr =
                    flowIdFromRoute != null ? Number(flowIdFromRoute) : NaN;
                if (Number.isFinite(fr) && fr > 0) {
                    flowIdNum = fr;
                }
            }

            const prevDraftElIdRaw = (blockConfig as Record<string, unknown>)
                .elementId;
            const prevDraftElId =
                typeof prevDraftElIdRaw === "number"
                    ? prevDraftElIdRaw
                    : Number(String(prevDraftElIdRaw ?? "").trim());
            const prevApiElForDraft =
                Number.isFinite(prevDraftElId) && prevDraftElId > 0
                    ? (ivrFull?.elements as IvrApiElement[] | undefined)?.find(
                          (e) => Number(e.elementId) === prevDraftElId
                      )
                    : undefined;
            const prevApiRec = prevApiElForDraft as
                | Record<string, unknown>
                | undefined;
            const previousElementDraftData =
                prevApiRec?.draftData ?? prevApiRec?.DraftData;

            const payloadOptions = {
                existingApiElements:
                    (ivrFull?.elements as
                        | { elementId?: number | null; elementType?: string | null }[]
                        | undefined) ?? [],
                graphElementIds: getGraphElementIds?.() ?? [],
                graphEndElementIds: getGraphEndElementIds?.() ?? [],
                entryElementId: (
                    ivrFull?.configuration as
                        | { entryElementId?: number | null }
                        | undefined
                )?.entryElementId ?? null,
                previousElementDraftData,
                ivrSchedulesForDraft: [...resolveIvrSchedulesServerTruth(ivrFull)],
            };

            if (
                shouldPostNewIvrConfiguratorElement(
                    blockConfig as Record<string, unknown>
                ) &&
                Number.isFinite(flowIdNum) &&
                flowIdNum > 0
            ) {
                setSavingIvrElement(true);
                try {
                    const payload = blockConfigToCreateElementPayload(
                        flowIdNum,
                        blockConfig as Record<string, unknown>,
                        payloadOptions
                    );
                    const res = await ivrConfigMutationRequest(
                        dispatch,
                        postIvrConfigElement(
                            payload,
                            jwtToken?.jwtToken ?? undefined
                        )
                    );
                    if (res === false) {
                        return;
                    }
                    const newId = extractCreatedElementIdFromResponse(res);
                    if (newId != null) {
                        const fromRaw = (blockConfig as Record<string, unknown>)
                            .fromElementId;
                        const fromElForRoute =
                            typeof fromRaw === "number"
                                ? fromRaw
                                : Number(String(fromRaw ?? "").trim());
                        const linkedFromStart =
                            blockConfig.configSourceNodeId ===
                            IVR_BASE_NODE_ID;
                        const configSourceNodeIdStr =
                            (blockConfig.configSourceNodeId as string) ?? "";
                        const { routeType: resolvedRouteType, routeValue: resolvedRouteValue } =
                            resolveIvrPostRouteTypeValue(configSourceNodeIdStr);
                        const schedulerSlotLinkUsesSchedulesOnly =
                            configSourceNodeIdStr.includes("-sched-slot-") ||
                            configSourceNodeIdStr.includes("-day-");

                        const postRaw = res?.data as Record<string, unknown> | null | undefined;
                        const postData = (postRaw?.data ?? postRaw) as Record<string, unknown> | undefined;
                        const responseAudios =
                            postData && typeof postData === "object" && Array.isArray(postData.audios)
                                ? (postData.audios as IvrApiElement["audios"])
                                : null;

                        const draftFromResponse =
                            postData != null &&
                            typeof postData === "object" &&
                            "draftData" in postData &&
                            postData.draftData != null &&
                            postData.draftData !== ""
                                ? String(postData.draftData)
                                : payload.draftData ?? null;

                        const newElement: IvrApiElement =
                            augmentSchedulerApiElementWithClientBlockConfig(
                                {
                                    flowId: flowIdNum,
                                    elementId: newId,
                                    name: payload.name,
                                    elementType: payload.elementType,
                                    audios:
                                        responseAudios ?? payload.audios ?? null,
                                    elementData: payload.elementData ?? null,
                                    draftData: draftFromResponse,
                            minDigits: payload.minDigits ?? null,
                            maxDigits: payload.maxDigits ?? null,
                            terminators: payload.terminators ?? null,
                            regexp: payload.regexp ?? null,
                            digitTimeOut: payload.digitTimeOut ?? null,
                            timeOut: payload.timeOut ?? null,
                                    silencePre: payload.silencePre ?? null,
                                    silencePost: payload.silencePost ?? null,
                                },
                                blockConfig as Record<string, unknown>
                            );
                        const updatedElements = [
                            ...((ivrFull?.elements ?? []) as IvrApiElement[]),
                            newElement,
                        ];
                        const updatedRoutes = [
                            ...((ivrFull?.routes ?? []) as IvrApiRoute[]),
                        ];
                        const updatedSchedules = [
                            ...((ivrFull?.schedules ?? []) as IvrApiSchedule[]),
                        ];
                        const serverSchedSnap = (
                            (ivrFull as Record<string, unknown> | undefined)
                                ?._ivrSchedulesServerSnapshot ?? []
                        ) as IvrApiSchedule[];
                        let scheduleAppendIndex: number | null = null;
                        let entryElementId =
                            (
                                ivrFull?.configuration as
                                    | { entryElementId?: number | null }
                                    | undefined
                            )?.entryElementId ?? null;

                        blockConfig.elementId = newId;
                        blockConfig.flowId = flowIdNum;
                        delete (blockConfig as Record<string, unknown>)
                            .fromElementId;

                        if (responseAudios && responseAudios.length > 0) {
                            const newIds = responseAudios.map((a) => a.audioId);
                            const newMeta = responseAudios.map((a) => ({
                                audioId: a.audioId,
                                audioName: a.audioName,
                            }));
                            if (startConfiguration === "Play") {
                                blockConfig.playAudioIds = newIds;
                                blockConfig.playAudioMeta = newMeta;
                                setSelectedPlayAudioIds(newIds);
                            } else if (startConfiguration === "Menu") {
                                blockConfig.menuAudioIds = newIds;
                                blockConfig.menuAudioMeta = newMeta;
                                setMenuAudioIds(newIds);
                            }
                        }

                        if (linkedFromStart) {
                            const conf = ivrFull?.configuration as
                                | {
                                      name?: string | null;
                                      description?: string | null;
                                      branchId?: number | null;
                                  }
                                | undefined;
                            const jwt = jwtToken?.jwtToken ?? undefined;
                            try {
                                await putIvrConfigConfiguration(
                                    {
                                        flowId: flowIdNum,
                                        entryElementId: newId,
                                        name: conf?.name ?? null,
                                        description: conf?.description ?? null,
                                        branchId: conf?.branchId ?? null,
                                    },
                                    jwt
                                );
                                entryElementId = newId;
                            } catch {
                                dispatch(showError());
                                return;
                            }
                        }

                        if (
                            !linkedFromStart &&
                            Number.isFinite(fromElForRoute) &&
                            fromElForRoute >= 0 &&
                            !schedulerSlotLinkUsesSchedulesOnly
                        ) {
                            const routes = (ivrFull?.routes ?? []) as IvrApiRoute[];
                            const jwt = jwtToken?.jwtToken ?? undefined;
                            const routePriority = nextIvrRoutePriority(
                                fromElForRoute,
                                routes
                            );
                            try {
                                const routeRes = await postIvrConfigRoute(
                                    {
                                        flowId: flowIdNum,
                                        fromElementId: fromElForRoute,
                                        routeType: resolvedRouteType,
                                        routeValue: resolvedRouteValue,
                                        toElementId: newId,
                                        priority: routePriority,
                                        isActive: true,
                                    },
                                    jwt
                                );
                                const rd = routeRes?.data?.data as
                                    | Partial<IvrApiRoute>
                                    | undefined;
                                updatedRoutes.push({
                                    id: rd?.id,
                                    flowId: rd?.flowId ?? flowIdNum,
                                    fromElementId:
                                        rd?.fromElementId ?? fromElForRoute,
                                    routeType:
                                        rd?.routeType ?? resolvedRouteType,
                                    routeValue:
                                        rd?.routeValue ?? resolvedRouteValue,
                                    toElementId: rd?.toElementId ?? newId,
                                    priority: rd?.priority ?? routePriority,
                                    isActive: rd?.isActive ?? true,
                                    tries: rd?.tries ?? null,
                                });
                            } catch {
                                dispatch(showError());
                                return;
                            }
                        }

                        if (
                            !linkedFromStart &&
                            Number.isFinite(fromElForRoute) &&
                            fromElForRoute >= 0
                        ) {
                            const sourceChainNodeId =
                                configSourceNodeIdStr || undefined;
                            let parentSchedulerNode: Node<IVRBlockData> | null | undefined = null;
                            let parentSchedulerConfigNodeId: string | null = null;
                            if (sourceChainNodeId) {
                                const chainNode = resolveLatestNode?.(sourceChainNodeId);
                                const parentId = chainNode?.data?.configParentId;
                                if (parentId) {
                                    const pNode = resolveLatestNode?.(parentId);
                                    if (
                                        pNode?.data?.blockConfig?.elementType === "SCHEDULER" ||
                                        pNode?.data?.blockConfig?.startConfiguration === "Scheduler"
                                    ) {
                                        parentSchedulerNode = pNode;
                                        parentSchedulerConfigNodeId = parentId;
                                    }
                                }
                            }
                            if (parentSchedulerNode) {
                                scheduleAppendIndex = updatedSchedules.length;
                                const jwt = jwtToken?.jwtToken ?? undefined;
                                const schedCfg = parentSchedulerNode.data.blockConfig as Record<string, unknown>;
                                const schedulerElId = fromElForRoute;
                                const allSchedRows = [
                                    ...buildSchedulerSlotRowsFromBlockConfig(
                                        schedCfg,
                                        newId
                                    ),
                                ].sort(
                                    (a, b) =>
                                        (a.priority ?? 0) - (b.priority ?? 0)
                                );

                                const parentSchedKind =
                                    normalizeIvrSchedulerScheduleKind(
                                        schedCfg.schedulerScheduleKind
                                    );
                                const deferScheduleUntilBlockLink =
                                    parentSchedKind === "daysweek" ||
                                    parentSchedKind === "once_advanced" ||
                                    parentSchedKind === "daysweek_advanced";

                                let matchedSchedRows: IvrSchedulerSlotRow[] =
                                    [];
                                let matchedSlotIndex = -1;
                                if (parentSchedulerConfigNodeId && sourceChainNodeId) {
                                    const slotPrefix = `${IVR_CHAIN_PREFIX}${parentSchedulerConfigNodeId}-sched-slot-`;
                                    if (sourceChainNodeId.startsWith(slotPrefix)) {
                                        const tail = sourceChainNodeId.slice(slotPrefix.length);
                                        const si = ivrSchedulerSlotRowIndexFromSlotSuffix(
                                            tail,
                                            allSchedRows
                                        );
                                        if (si >= 0 && si < allSchedRows.length) {
                                            matchedSlotIndex = si;
                                            matchedSchedRows = [allSchedRows[si]!];
                                        }
                                    } else {
                                        const dayDow =
                                            ivrSchedulerWeekdayChainDayOfWeek(
                                                sourceChainNodeId
                                            );
                                        if (dayDow != null) {
                                            const mask =
                                                schedulerDayOfWeekToDaysMask(
                                                    dayDow
                                                );
                                            const si = allSchedRows.findIndex(
                                                (r) =>
                                                    (Number(r.daysMask) || 0) ===
                                                    mask
                                            );
                                            if (si >= 0) {
                                                matchedSlotIndex = si;
                                                matchedSchedRows = [
                                                    allSchedRows[si]!,
                                                ];
                                            }
                                        } else if (
                                            sourceChainNodeId.endsWith(
                                                "-daily-primary"
                                            ) &&
                                            allSchedRows.length >= 1
                                        ) {
                                            matchedSlotIndex = 0;
                                            matchedSchedRows = [allSchedRows[0]!];
                                        } else if (
                                            sourceChainNodeId.endsWith(
                                                "-daily-rest"
                                            ) &&
                                            allSchedRows.length >= 2
                                        ) {
                                            matchedSlotIndex = 1;
                                            matchedSchedRows = [allSchedRows[1]!];
                                        } else if (allSchedRows.length > 1) {
                                            matchedSchedRows = [];
                                        }
                                    }
                                }
                                if (
                                    sourceChainNodeId &&
                                    sourceChainNodeId.endsWith("-sched-others")
                                ) {
                                    matchedSchedRows = [];
                                }

                                if (
                                    matchedSchedRows.length === 0 &&
                                    !deferScheduleUntilBlockLink &&
                                    allSchedRows.length === 1
                                ) {
                                    matchedSlotIndex = 0;
                                    matchedSchedRows = [allSchedRows[0]!];
                                }

                                matchedSchedRows = matchedSchedRows.map((r) => ({
                                    ...r,
                                    activeToId: newId,
                                }));

                                const schedRowsForApi =
                                    filterIvrSchedulerSlotRowsForScheduleApi(
                                        parentSchedKind,
                                        matchedSchedRows
                                    );

                                const apiSchedules =
                                    schedRowsForApi.length > 0
                                        ? slotRowsToSchedules(
                                              schedulerElId,
                                              flowIdNum,
                                              schedRowsForApi
                                          )
                                        : [];
                                let nextPrio =
                                    nextIvrSchedulePostPriorityFromSources(
                                        schedulerElId,
                                        updatedSchedules,
                                        serverSchedSnap
                                    );
                                try {
                                    for (const s of apiSchedules) {
                                        const inactiveHit =
                                            findInactiveSchedulerSlotMatchingPostCandidate(
                                                schedulerElId,
                                                s,
                                                [
                                                    updatedSchedules,
                                                    serverSchedSnap,
                                                ]
                                            );
                                        const hitId = inactiveHit
                                            ? ivrRawScheduleRowId(
                                                  inactiveHit
                                              )
                                            : -1;
                                        if (
                                            inactiveHit &&
                                            hitId > 0
                                        ) {
                                            const schedPut =
                                                await ivrConfigMutationRequest(
                                                    dispatch,
                                                    putIvrConfigSchedule(
                                                        {
                                                            id: hitId,
                                                            daysMask:
                                                                s.daysMask,
                                                            fromTime:
                                                                s.fromTime,
                                                            toTime: s.toTime,
                                                            activeToId:
                                                                s.activeToId,
                                                            isActive: true,
                                                        },
                                                        jwt
                                                    )
                                                );
                                            if (schedPut === false) {
                                                return;
                                            }
                                            const putRow: IvrApiSchedule = {
                                                ...inactiveHit,
                                                id: hitId,
                                                flowId:
                                                    s.flowId ?? flowIdNum,
                                                elementId: s.elementId,
                                                priority:
                                                    inactiveHit.priority ??
                                                    s.priority,
                                                daysMask: s.daysMask,
                                                fromTime: s.fromTime,
                                                toTime: s.toTime,
                                                activeToId: s.activeToId,
                                                isActive: true,
                                            };
                                            const ix = updatedSchedules.findIndex(
                                                (x) =>
                                                    ivrRawScheduleRowId(x) ===
                                                    hitId
                                            );
                                            if (ix >= 0)
                                                updatedSchedules[ix] = putRow;
                                            else updatedSchedules.push(putRow);
                                            continue;
                                        }
                                        const prio = nextPrio++;
                                        const schedPost = await ivrConfigMutationRequest(
                                            dispatch,
                                            postIvrConfigSchedule(
                                                {
                                                    flowId: s.flowId ?? flowIdNum,
                                                    elementId: s.elementId,
                                                    priority: prio,
                                                    daysMask: s.daysMask,
                                                    fromTime: s.fromTime,
                                                    toTime: s.toTime,
                                                    activeToId: s.activeToId,
                                                    isActive: true,
                                                },
                                                jwt
                                            )
                                        );
                                        if (schedPost === false) {
                                            return;
                                        }
                                        const schedPostRoot = schedPost.data as {
                                            data?: Partial<IvrApiSchedule>;
                                        };
                                        const createdSched = schedPostRoot?.data;
                                        const createdSchedId =
                                            createdSched?.id != null
                                                ? Number(createdSched.id)
                                                : NaN;
                                        updatedSchedules.push({
                                            id:
                                                Number.isFinite(createdSchedId) &&
                                                createdSchedId > 0
                                                    ? createdSchedId
                                                    : undefined,
                                            flowId:
                                                createdSched?.flowId ??
                                                s.flowId ??
                                                flowIdNum,
                                            elementId:
                                                createdSched?.elementId ??
                                                s.elementId,
                                            priority:
                                                createdSched?.priority ?? prio,
                                            daysMask:
                                                createdSched?.daysMask ??
                                                s.daysMask,
                                            fromTime:
                                                createdSched?.fromTime ??
                                                s.fromTime,
                                            toTime:
                                                createdSched?.toTime ?? s.toTime,
                                            activeToId:
                                                createdSched?.activeToId ??
                                                newId,
                                            isActive:
                                                createdSched?.isActive ?? true,
                                        });
                                    }
                                } catch {
                                    dispatch(showError());
                                    return;
                                }

                                if (
                                    matchedSlotIndex >= 0 &&
                                    matchedSlotIndex < allSchedRows.length
                                ) {
                                    const createdForSlot = updatedSchedules
                                        .slice(scheduleAppendIndex)
                                        .find(
                                            (sch) =>
                                                ivrRawScheduleElementId(sch) ===
                                                    schedulerElId &&
                                                Number(
                                                    (sch as Record<string, unknown>)
                                                        .daysMask ??
                                                        (sch as Record<string, unknown>)
                                                            .DaysMask
                                                ) ===
                                                    (Number(
                                                        allSchedRows[matchedSlotIndex]!
                                                            .daysMask
                                                    ) ||
                                                        0) &&
                                                ivrRawScheduleActiveToId(sch) ===
                                                    newId
                                        );
                                    const patchedSlotRows = allSchedRows.map(
                                        (row, idx) =>
                                            idx === matchedSlotIndex
                                                ? {
                                                      ...row,
                                                      activeToId: newId,
                                                      ...(createdForSlot &&
                                                      ivrRawScheduleRowId(
                                                          createdForSlot
                                                      ) > 0
                                                          ? {
                                                                id: ivrRawScheduleRowId(
                                                                    createdForSlot
                                                                ),
                                                            }
                                                          : {}),
                                                  }
                                                : row
                                    );
                                    const schedElIdx = updatedElements.findIndex(
                                        (e) => Number(e.elementId) === schedulerElId
                                    );
                                    if (schedElIdx >= 0) {
                                        updatedElements[schedElIdx] =
                                            augmentSchedulerApiElementWithClientBlockConfig(
                                                updatedElements[schedElIdx]!,
                                                {
                                                    ...schedCfg,
                                                    schedulerSlotRows:
                                                        patchedSlotRows,
                                                    schedulerActiveToId: newId,
                                                }
                                            );
                                    }
                                }
                            }
                        }

                        if (payload.elementType === "OPERATOR") {
                            const jwtAuto = jwtToken?.jwtToken ?? undefined;
                            if (!jwtAuto) {
                                dispatch(showError());
                                return;
                            }
                            const endElementId = computeNextIvrEndElementId(
                                updatedElements,
                                getGraphEndElementIds?.() ?? []
                            );
                            const endRes = await ivrConfigMutationRequest(
                                dispatch,
                                postIvrConfigElement(
                                    {
                                        flowId: flowIdNum,
                                        elementId: endElementId,
                                        name: "END",
                                        elementType: "END",
                                    },
                                    jwtAuto
                                )
                            );
                            if (endRes === false) {
                                return;
                            }
                            const endNewId =
                                extractCreatedElementIdFromResponse(endRes) ??
                                endElementId;
                            updatedElements.push({
                                flowId: flowIdNum,
                                elementId: endNewId,
                                name: "END",
                                elementType: "END",
                                audios: null,
                                elementData: null,
                                minDigits: null,
                                maxDigits: null,
                                terminators: null,
                                regexp: null,
                                digitTimeOut: null,
                                timeOut: null,
                                silencePre: null,
                                silencePost: null,
                            });

                            const hunterToEndPriority = nextIvrRoutePriority(
                                newId,
                                updatedRoutes
                            );
                            try {
                                const hunterToEndRaw = await postIvrConfigRoute(
                                    {
                                        flowId: flowIdNum,
                                        fromElementId: newId,
                                        routeType: "DEFAULT",
                                        routeValue: "",
                                        toElementId: endNewId,
                                        priority: hunterToEndPriority,
                                        isActive: true,
                                    },
                                    jwtAuto
                                );
                                const hunterRd = hunterToEndRaw?.data?.data as
                                    | Partial<IvrApiRoute>
                                    | undefined;
                                updatedRoutes.push({
                                    id: hunterRd?.id,
                                    flowId: hunterRd?.flowId ?? flowIdNum,
                                    fromElementId:
                                        hunterRd?.fromElementId ?? newId,
                                    routeType: hunterRd?.routeType ?? "DEFAULT",
                                    routeValue: hunterRd?.routeValue ?? "",
                                    toElementId:
                                        hunterRd?.toElementId ?? endNewId,
                                    priority:
                                        hunterRd?.priority ??
                                        hunterToEndPriority,
                                    isActive: hunterRd?.isActive ?? true,
                                    tries: hunterRd?.tries ?? null,
                                });
                            } catch {
                                dispatch(showError());
                                return;
                            }
                        }

                        const schedulesForDispatch = updatedSchedules;

                        dispatch(
                            setIvrSchemeGraphApiSlice({
                                elements: updatedElements,
                                routes: updatedRoutes,
                                schedules: schedulesForDispatch,
                                ...(scheduleAppendIndex != null
                                    ? {
                                          schedulesServerSnapshotReplace:
                                              schedulesForDispatch,
                                      }
                                    : {}),
                                configurationPatch: { entryElementId },
                            })
                        );
                        if (payload.elementType === "OPERATOR") {
                            dispatch(
                                getIVRConfigurationFullById(
                                    flowIdNum,
                                    jwtToken?.jwtToken
                                )
                            );
                        }
                    } else {
                        onDismissSettings?.();
                        return;
                    }
                } catch {
                    dispatch(showError());
                    return;
                } finally {
                    setSavingIvrElement(false);
                }
            } else if (
                !shouldPostNewIvrConfiguratorElement(
                    blockConfig as Record<string, unknown>
                ) &&
                Number.isFinite(flowIdNum) &&
                flowIdNum > 0
            ) {
                const rawEl = (blockConfig as Record<string, unknown>)
                    .elementId;
                const elId =
                    typeof rawEl === "number"
                        ? rawEl
                        : Number(String(rawEl).trim());
                if (Number.isFinite(elId) && elId > 0) {
                    setSavingIvrElement(true);
                    try {
                        blockConfig.flowId = flowIdNum;

                        const schedulesSnapshot = [
                            ...resolveIvrSchedulesServerTruth(ivrFull),
                        ];
                        let schedulesForDraft = [...schedulesSnapshot];

                        const bcPut = blockConfig as Record<string, unknown>;
                        const schedKindPut = normalizeIvrSchedulerScheduleKind(
                            bcPut.schedulerScheduleKind ?? "daysweek"
                        );
                        const elTypePut = String(
                            bcPut.elementType ?? ""
                        ).toUpperCase();
                        const weekdayCfgDays =
                            (bcPut.schedulerDays as
                                | {
                                      dayOfWeek: number;
                                      enabled?: boolean;
                                      start?: string;
                                      end?: string;
                                  }[]
                                | undefined) ?? [];

                        const weekdayStatesForSchedulerPut =
                            weekdayCfgDays.map((d) => ({
                                dayOfWeek: Number(d.dayOfWeek),
                                enabled: !!d.enabled,
                                startHHMM: String(d.start ?? "").trim(),
                                endHHMM: String(d.end ?? "").trim(),
                            }));

                        const shouldBuildSchedulerUpdates =
                            elTypePut === "SCHEDULER" &&
                            schedKindPut === "daysweek";

                        const shouldBuildOnceAdvancedUpdates =
                            elTypePut === "SCHEDULER" &&
                            schedKindPut === "once_advanced";

                        const weekdaySchedulerUpdatePayloads =
                            shouldBuildSchedulerUpdates
                                ? buildIvrSchedulerUpdateSchedulersPayload(
                                      elId,
                                      schedulesSnapshot,
                                      weekdayStatesForSchedulerPut,
                                      savedSchedulerSlotRowsRef.current
                                  )
                                : [];

                        const onceAdvancedSchedulerUpdatePayloads =
                            shouldBuildOnceAdvancedUpdates
                                ? buildIvrOnceAdvancedUpdateSchedulersPayload(
                                      elId,
                                      schedulesSnapshot,
                                      ((bcPut.schedulerSlotRows as
                                          | IvrSchedulerSlotRow[]
                                          | undefined) ?? [])
                                  )
                                : [];

                        const schedulerKindChangedOnPut =
                            elTypePut === "SCHEDULER" &&
                            normalizeIvrSchedulerScheduleKind(
                                (prevCfg as Record<string, unknown>)
                                    ?.schedulerScheduleKind
                            ) !== schedKindPut;

                        const deactivateOnKindChangePayloads =
                            schedulerKindChangedOnPut
                                ? buildIvrSchedulerDeactivateAllForElementPayload(
                                      elId,
                                      schedulesSnapshot
                                  )
                                : [];

                        if (deactivateOnKindChangePayloads.length > 0) {
                            schedulesForDraft = schedulesForDraft.map((row) => {
                                const u = deactivateOnKindChangePayloads.find(
                                    (x) => x.id === row.id
                                );
                                if (!u) return row;
                                return { ...row, isActive: u.isActive };
                            });
                        }

                        const schedulerUpdatePayloads = [
                            ...deactivateOnKindChangePayloads,
                            ...weekdaySchedulerUpdatePayloads,
                            ...onceAdvancedSchedulerUpdatePayloads,
                        ];

                        const putPayloadOptions = {
                            ...payloadOptions,
                            ivrSchedulesForDraft: schedulesForDraft,
                            ...(schedulerUpdatePayloads.length > 0
                                ? {
                                      updateSchedulers: schedulerUpdatePayloads,
                                  }
                                : {}),
                        };
                        const payload = blockConfigToCreateElementPayload(
                            flowIdNum,
                            blockConfig as Record<string, unknown>,
                            putPayloadOptions
                        );
                        const putRes = await ivrConfigMutationRequest(
                            dispatch,
                            putIvrConfigElement(
                                payload,
                                jwtToken?.jwtToken ?? undefined
                            )
                        );
                        if (putRes === false) {
                            return;
                        }

                        if (startConfiguration === "Menu") {
                            const removedKeys = savedMenuDtmfKeysRef.current.filter(
                                (k) => !menuDtmfKeys.includes(k)
                            );
                            if (removedKeys.length > 0) {
                                const existingRoutes = (ivrFull?.routes ?? []) as IvrApiRoute[];
                                const jwt = jwtToken?.jwtToken ?? undefined;
                                for (const key of removedKeys) {
                                    const matchingRoutes = existingRoutes.filter(
                                        (r) =>
                                            r.fromElementId === elId &&
                                            r.id != null &&
                                            ((r.routeType === "DTMF" && r.routeValue === key) ||
                                                (r.routeType === "DTMF_EXCEEDED" && r.routeValue === key))
                                    );
                                    for (const route of matchingRoutes) {
                                        try {
                                            await deleteIvrConfigRoute(route.id!, jwt);
                                        } catch {
                                            /* best-effort */
                                        }
                                    }
                                }
                                savedMenuDtmfKeysRef.current = menuDtmfKeys;
                            }
                        }

                        const putRaw = putRes?.data as Record<string, unknown> | null | undefined;
                        const putData = (putRaw?.data ?? putRaw) as Record<string, unknown> | undefined;
                        if (putData && typeof putData === "object" && Array.isArray(putData.audios)) {
                            const responseAudios = putData.audios as IvrApiElement["audios"];
                            const existingElements = [...((ivrFull?.elements ?? []) as IvrApiElement[])];
                            const elementIdx = existingElements.findIndex((e) => e.elementId === elId);
                            if (elementIdx >= 0) {
                                existingElements[elementIdx] = {
                                    ...existingElements[elementIdx],
                                    audios: responseAudios,
                                };
                                const currentEntryElementId =
                                    (
                                        ivrFull?.configuration as
                                            | { entryElementId?: number | null }
                                            | undefined
                                    )?.entryElementId ?? null;
                                dispatch(
                                    setIvrSchemeGraphApiSlice({
                                        elements: existingElements,
                                        routes: (ivrFull?.routes ?? []) as unknown[],
                                        schedules: (ivrFull?.schedules ?? []) as unknown[],
                                        configurationPatch: { entryElementId: currentEntryElementId },
                                    })
                                );
                            }

                            if (responseAudios && responseAudios.length > 0) {
                                const newIds = responseAudios.map((a) => a.audioId);
                                const newMeta = responseAudios.map((a) => ({
                                    audioId: a.audioId,
                                    audioName: a.audioName,
                                }));
                                if (startConfiguration === "Play") {
                                    blockConfig.playAudioIds = newIds;
                                    blockConfig.playAudioMeta = newMeta;
                                    setSelectedPlayAudioIds(newIds);
                                } else if (startConfiguration === "Menu") {
                                    blockConfig.menuAudioIds = newIds;
                                    blockConfig.menuAudioMeta = newMeta;
                                    setMenuAudioIds(newIds);
                                }
                            }
                        }

                        if (startConfiguration === "Scheduler") {
                            const currentEntryElementId =
                                (
                                    ivrFull?.configuration as
                                        | { entryElementId?: number | null }
                                        | undefined
                                )?.entryElementId ?? null;
                            const baseElements = [
                                ...((ivrFull?.elements ?? []) as IvrApiElement[]),
                            ];
                            const idx = baseElements.findIndex(
                                (e) => e.elementId === elId
                            );
                            let draftStr: string | null = null;
                            if (putData != null && typeof putData === "object") {
                                const pr = putData as Record<string, unknown>;
                                const d = pr.draftData ?? pr.DraftData;
                                if (d != null && d !== "") {
                                    draftStr = String(d);
                                }
                            }
                            if (idx >= 0) {
                                const mergedEl =
                                    augmentSchedulerApiElementWithClientBlockConfig(
                                        {
                                            ...baseElements[idx],
                                            draftData:
                                                draftStr ??
                                                payload.draftData ??
                                                baseElements[idx].draftData ??
                                                null,
                                        },
                                        blockConfig as Record<string, unknown>
                                    );
                                baseElements[idx] = mergedEl;
                            }
                            const schedulesAfterSchedulerPut =
                                schedulerUpdatePayloads.length > 0
                                    ? schedulesForDraft.map((row) => {
                                          const u = schedulerUpdatePayloads.find(
                                              (x) => x.id === row.id
                                          );
                                          if (!u) return row;
                                          return {
                                              ...row,
                                              fromTime: u.fromTime,
                                              toTime: u.toTime,
                                              isActive: u.isActive,
                                          };
                                      })
                                    : schedulesForDraft;
                            dispatch(
                                setIvrSchemeGraphApiSlice({
                                    elements: baseElements,
                                    routes: (ivrFull?.routes ?? []) as unknown[],
                                    schedules:
                                        schedulesAfterSchedulerPut as unknown[],
                                    configurationPatch: {
                                        entryElementId: currentEntryElementId,
                                    },
                                })
                            );
                            const refreshedSlotRows =
                                slotRowsFromApiSchedulesForElement(
                                    elId,
                                    schedulesAfterSchedulerPut as IvrApiSchedule[]
                                );
                            savedSchedulerSlotRowsRef.current =
                                refreshedSlotRows.length > 0
                                    ? refreshedSlotRows
                                    : ((blockConfig.schedulerSlotRows as
                                          | IvrSchedulerSlotRow[]
                                          | undefined) ?? []);
                        }
                    } catch {
                        dispatch(showError());
                        return;
                    } finally {
                        setSavingIvrElement(false);
                    }
                }
            }

            setIvrBlockErrorFields(null);
            onGenerateChain?.(node.id, blockConfig);
            return;
        }
        onSave?.(node.id, blockConfig);
    };

    return (
        <div
            className={classNames("ivr-block-settings-panel p-4", {
                "ivr-block-settings-panel--readonly": isReadOnlyIVRScheme,
            })}
        >
            <fieldset
                disabled={isReadOnlyIVRScheme}
                className="m-0 p-0 border-none"
            >
            {/*{displayId !== 'start-config-3' && <div className="flex align-items-start justify-content-end mb-3">*/}
            {/*    /!*<h5 className="m-0 font-semibold">{blockTypeLabel}</h5>*!/*/}
            {/*    <span className="text-color-secondary text-sm">{displayId}</span>*/}
            {/*</div>}*/}

            {displayId !== 'start-config-3' &&<div className="flex align-items-center gap-2 mb-2">
                <IconBadge
                    shape={data.shape}
                    muted={data.muted}
                    accentColor={iconBadgeAccentColor}
                >
                    {data.icon && <i className={data.icon} />}
                </IconBadge>
                <span>{data.label || ""}</span>
            </div>}

            {isStartConfig && (
                <div className="mb-4">
                    <label className="block font-semibold mb-2">
                        Конфігурація
                    </label>
                    <Dropdown
                        value={startConfiguration}
                        onChange={(e) => {
                            const nextConf = (e.value ?? "Play") as StartConfigurationType;
                            setStartConfiguration(nextConf);
                            setIvrBlockErrorFields((prev) => {
                                if (!prev) return null;
                                const nextErrors = { ...prev };
                                if (nextConf !== "Play") {
                                    delete nextErrors.playAudios;
                                }
                                if (nextConf !== "Menu") {
                                    delete nextErrors.menuAudios;
                                }
                                if (nextConf !== "AgentHunter") {
                                    delete nextErrors.hunterQueue;
                                }
                                return Object.keys(nextErrors).length ? nextErrors : null;
                            });
                            if (nextConf !== "Play") {
                                setSelectedAudioGroupId(null);
                                setSelectedPlayAudioIds([]);
                            }
                            if (nextConf !== "Menu") {
                                setMenuAudioGroupId(null);
                                setMenuAudioIds([]);
                                setMenuDtmfKeys([]);
                                setMenuSilensPre(null);
                                setMenuSilensPost(null);
                                setMenuTimeout(null);
                                setMenuTimeoutRetries(null);
                                setMenuRetries(null);
                                setMenuDtmfRetries({});
                            }
                        }}
                        options={START_CONFIGURATION_OPTIONS}
                        optionLabel="label"
                        optionValue="id"
                        placeholder="Оберіть конфігурацію"
                        className="w-full mb-3"
                    />

                    {startConfiguration !== "End" &&<Divider/>}

                    {startConfiguration !== "End" && <div className="mb-3">
                        <label className="block font-semibold mb-2">
                            Назва
                        </label>
                        <InputText
                            value={startConfigTitle}
                            onChange={(e) =>
                                setStartConfigTitle(e.target.value)
                            }
                            placeholder="Введіть назву конфігурації"
                            className="w-full"
                        />
                    </div>}

                    {startConfiguration === "Play" && (
                        <>
                            <div className="mb-3">
                                <h5>Audio</h5>
                                <label className="block font-semibold mb-2">
                                    Група аудіо
                                </label>
                                <Dropdown
                                    filter
                                    value={normalizeAudioGroupId(selectedAudioGroupId)}
                                    onChange={(e) => {
                                        setSelectedAudioGroupId(
                                            normalizeAudioGroupId(e.value)
                                        );
                                    }}
                                    options={playAudioGroupDropdownOptions}
                                    optionLabel="groupName"
                                    optionValue="groupId"
                                    placeholder="Виберіть групу"
                                    className="w-full"
                                />
                            </div>

                            <div className="mb-2" ref={playAudiosFieldRef}>
                                <label
                                    className={classNames("block font-semibold mb-2", {
                                        "p-error": ivrBlockErrorFields?.playAudios,
                                    })}
                                >
                                    Аудіо з групи
                                </label>
                                <MultiSelect
                                    filter
                                    value={asMultiSelectArray(
                                        selectedPlayAudioIdsForMultiSelect
                                    )}
                                    onChange={(e) => {
                                        const nextInGroup = asMultiSelectArray(
                                            (e.value ?? []) as number | number[]
                                        ) as number[];
                                        if (
                                            normalizeAudioGroupId(
                                                selectedAudioGroupId
                                            ) == null
                                        ) {
                                            setSelectedPlayAudioIds(nextInGroup);
                                            return;
                                        }
                                        const groupIds = new Set(currentGroupAudioIds);
                                        setSelectedPlayAudioIds((prev) => {
                                            const prevInGroup = prev.filter((id) =>
                                                groupIds.has(id)
                                            );
                                            const nextSet = new Set(nextInGroup);
                                            const keptInGroup = prevInGroup.filter((id) =>
                                                nextSet.has(id)
                                            );
                                            const addedInGroup = nextInGroup.filter(
                                                (id) => !prevInGroup.includes(id)
                                            );
                                            const keptOtherGroups = prev.filter(
                                                (id) => !groupIds.has(id)
                                            );
                                            return [
                                                ...keptOtherGroups,
                                                ...keptInGroup,
                                                ...addedInGroup,
                                            ];
                                        });
                                    }}
                                    options={selectedPlayAudioMultiSelectOptions}
                                    optionLabel="audioName"
                                    optionValue="audioId"
                                    placeholder="Виберіть аудіо"
                                    className={classNames("w-full", {
                                        "p-invalid": ivrBlockErrorFields?.playAudios,
                                    })}
                                    disabled={selectedPlayAudioMultiSelectOptions.length === 0}
                                    display="chip"
                                    selectedItemTemplate={playIvrAudioSelectedItemTemplate}
                                />
                                {ivrBlockErrorFields?.playAudios && (
                                    <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                                        <span className="title p-error text-sm">
                                            {ivrBlockFieldErrorMessage(
                                                ivrBlockErrorFields.playAudios
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {selectedPlayAudios.length > 0 && (
                                <div className="mt-3 w-full min-w-0">
                                    <div className="font-semibold mb-2">
                                        Обрані аудіо
                                    </div>
                                    <DataTable
                                        key={[...selectedPlayAudioIds]
                                            .slice()
                                            .sort((a, b) => a - b)
                                            .join(",")}
                                        dataKey="audioId"
                                        value={selectedPlayAudios}
                                        reorderableRows
                                        onRowReorder={onPlayAudioRowReorder}
                                        className="w-full ivr-selected-audio-datatable"
                                        tableStyle={{ tableLayout: "fixed", width: "100%" }}
                                    >
                                        <Column
                                            rowReorder
                                            className="ivr-selected-audio-reorder-cell"
                                            style={{ width: "2.5rem" }}
                                        />
                                        <Column
                                            field="audioName"
                                            className="ivr-selected-audio-name-cell"
                                            style={{
                                                width: "calc(100% - 2.5rem - 7rem)",
                                            }}
                                            bodyStyle={{
                                                maxWidth: 0,
                                                minWidth: 0,
                                                overflow: "hidden",
                                                verticalAlign: "middle",
                                            }}
                                            body={(rowData: {
                                                audioName?: string;
                                                audioId: number;
                                            }) => {
                                                const full =
                                                    (rowData.audioName ?? "").trim() ||
                                                    `Audio #${rowData.audioId}`;
                                                const visible = formatSelectedAudioListLabel(full);
                                                return (
                                                    <div
                                                        className="ivr-selected-audio-name-text"
                                                        title={full}
                                                    >
                                                        {visible}
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Column
                                            className="px-0 ivr-selected-audio-actions-cell"
                                            headerStyle={{ width: "7rem" }}
                                            bodyStyle={{
                                                width: "7rem",
                                                minWidth: "7rem",
                                                maxWidth: "7rem",
                                                verticalAlign: "middle",
                                            }}
                                            body={(rowData) => (
                                                <div className="flex align-items-center justify-content-end gap-1 flex-nowrap">
                                                    {savedAudioIds.has(rowData.audioId) ? (
                                                        <Button
                                                            icon="pi pi-play"
                                                            className="p-button-secondary pr-0"
                                                            loading={
                                                                ivrAudioLoading ===
                                                                `play-${rowData.audioId}`
                                                            }
                                                            onClick={() =>
                                                                handlePlayIvrAudio(
                                                                    rowData.audioId,
                                                                    rowData.audioName,
                                                                    `play-${rowData.audioId}`
                                                                )
                                                            }
                                                            disabled={detailsLoading}
                                                        />
                                                    ) : (
                                                        <Button
                                                            icon={
                                                                hasAudioUpdatePermission
                                                                    ? "pi pi-pencil"
                                                                    : "pi pi-eye"
                                                            }
                                                            className="p-button-secondary pr-0"
                                                            loading={
                                                                detailsLoading &&
                                                                audioFieldLoadingKey ===
                                                                    `start-play-${rowData.audioId}`
                                                            }
                                                            onClick={() => {
                                                                dispatch(
                                                                    getAudioById(
                                                                        rowData.groupId,
                                                                        jwtToken?.jwtToken,
                                                                        true
                                                                    )
                                                                );
                                                                setAudioFieldLoadingKey(
                                                                    `start-play-${rowData.audioId}`
                                                                );
                                                                dispatch(
                                                                    setEditType(
                                                                        !!hasAudioUpdatePermission
                                                                    )
                                                                );
                                                            }}
                                                            disabled={detailsLoading}
                                                        />
                                                    )}
                                                    <Button
                                                        icon="pi pi-times"
                                                        className="p-button-secondary px-0"
                                                        onClick={() => {
                                                            if (savedAudioIds.has(rowData.audioId)) {
                                                                void (async () => {
                                                                    const ok =
                                                                        await removeIvrAudioFromSavedElement(
                                                                            rowData.audioId
                                                                        );
                                                                    if (!ok) return;
                                                                    setSelectedPlayAudioIds(
                                                                        (prev) =>
                                                                            prev.filter(
                                                                                (id) =>
                                                                                    id !==
                                                                                    rowData.audioId
                                                                            )
                                                                    );
                                                                })();
                                                            } else {
                                                                setSelectedPlayAudioIds(
                                                                    (prev) =>
                                                                        prev.filter(
                                                                            (id) =>
                                                                                id !==
                                                                                rowData.audioId
                                                                        )
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </DataTable>
                                    <AudioDialog />
                                </div>
                            )}

                            <Divider/>

                            <div className="mb-3">
                                <h5>Silens</h5>
                                <div className="p-fluid grid">
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Silens Pre
                                        </label>
                                        <InputNumber
                                            value={playSilensPre}
                                            onValueChange={(e) =>
                                                setPlaySilensPre(e.value ?? null)
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Silens Post
                                        </label>
                                        <InputNumber
                                            value={playSilensPost}
                                            onValueChange={(e) =>
                                                setPlaySilensPost(e.value ?? null)
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Divider/>

                            <div className="mb-3">
                                <h5>Timeout</h5>
                                <InputNumber
                                    value={playTimeout}
                                    onValueChange={(e) =>
                                        setPlayTimeout(e.value ?? null)
                                    }
                                    useGrouping={false}
                                    className="w-full"
                                    placeholder="0"
                                />
                            </div>
                        </>
                    )}

                    {startConfiguration === "Menu" && (
                        <>
                            <div className="mb-3">
                                <h5>Audio</h5>
                                <label className="block font-semibold mb-2">
                                    Група аудіо
                                </label>
                                <Dropdown
                                    filter
                                    value={normalizeAudioGroupId(menuAudioGroupId)}
                                    onChange={(e) => {
                                        setMenuAudioGroupId(
                                            normalizeAudioGroupId(e.value)
                                        );
                                    }}
                                    options={menuAudioGroupDropdownOptions}
                                    optionLabel="groupName"
                                    optionValue="groupId"
                                    placeholder="Виберіть групу"
                                    className="w-full"
                                />
                            </div>

                            <div className="mb-2" ref={menuAudiosFieldRef}>
                                <label
                                    className={classNames("block font-semibold mb-2", {
                                        "p-error": ivrBlockErrorFields?.menuAudios,
                                    })}
                                >
                                    Аудіо з групи
                                </label>
                                <MultiSelect
                                    filter
                                    value={asMultiSelectArray(menuAudioIdsForMultiSelect)}
                                    onChange={(e) => {
                                        const nextInGroup = asMultiSelectArray(
                                            (e.value ?? []) as number | number[]
                                        ) as number[];
                                        if (
                                            normalizeAudioGroupId(
                                                menuAudioGroupId
                                            ) == null
                                        ) {
                                            setMenuAudioIds(nextInGroup);
                                            return;
                                        }
                                        const groupIds = new Set(menuCurrentGroupAudioIds);
                                        setMenuAudioIds((prev) => {
                                            const prevInGroup = prev.filter((id) =>
                                                groupIds.has(id)
                                            );
                                            const nextSet = new Set(nextInGroup);
                                            const keptInGroup = prevInGroup.filter((id) =>
                                                nextSet.has(id)
                                            );
                                            const addedInGroup = nextInGroup.filter(
                                                (id) => !prevInGroup.includes(id)
                                            );
                                            const keptOtherGroups = prev.filter(
                                                (id) => !groupIds.has(id)
                                            );
                                            return [
                                                ...keptOtherGroups,
                                                ...keptInGroup,
                                                ...addedInGroup,
                                            ];
                                        });
                                    }}
                                    options={menuAudioMultiSelectOptions}
                                    optionLabel="audioName"
                                    optionValue="audioId"
                                    placeholder="Виберіть аудіо"
                                    className={classNames("w-full", {
                                        "p-invalid": ivrBlockErrorFields?.menuAudios,
                                    })}
                                    disabled={menuAudioMultiSelectOptions.length === 0}
                                    display="chip"
                                    selectedItemTemplate={menuIvrAudioSelectedItemTemplate}
                                />
                                {ivrBlockErrorFields?.menuAudios && (
                                    <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                                        <span className="title p-error text-sm">
                                            {ivrBlockFieldErrorMessage(
                                                ivrBlockErrorFields.menuAudios
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {selectedMenuAudios.length > 0 && (
                                <div className="mt-3 w-full min-w-0">
                                    <div className="font-semibold mb-2">
                                        Обрані аудіо
                                    </div>
                                    <DataTable
                                        key={[...menuAudioIds].slice().sort((a, b) => a - b).join(",")}
                                        dataKey="audioId"
                                        value={selectedMenuAudios}
                                        reorderableRows
                                        onRowReorder={onMenuAudioRowReorder}
                                        className="w-full ivr-selected-audio-datatable"
                                        tableStyle={{ tableLayout: "fixed", width: "100%" }}
                                    >
                                        <Column
                                            rowReorder
                                            className="ivr-selected-audio-reorder-cell"
                                            style={{ width: "2.5rem" }}
                                        />
                                        <Column
                                            field="audioName"
                                            className="ivr-selected-audio-name-cell"
                                            style={{
                                                width: "calc(100% - 2.5rem - 7rem)",
                                            }}
                                            bodyStyle={{
                                                maxWidth: 0,
                                                minWidth: 0,
                                                overflow: "hidden",
                                                verticalAlign: "middle",
                                            }}
                                            body={(rowData: {
                                                audioName?: string;
                                                audioId: number;
                                            }) => {
                                                const full =
                                                    (rowData.audioName ?? "").trim() ||
                                                    `Audio #${rowData.audioId}`;
                                                const visible = formatSelectedAudioListLabel(full);
                                                return (
                                                    <div
                                                        className="ivr-selected-audio-name-text"
                                                        title={full}
                                                    >
                                                        {visible}
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Column
                                            className="px-0 ivr-selected-audio-actions-cell"
                                            headerStyle={{ width: "7rem" }}
                                            bodyStyle={{
                                                width: "7rem",
                                                minWidth: "7rem",
                                                maxWidth: "7rem",
                                                verticalAlign: "middle",
                                            }}
                                            body={(rowData) => (
                                                <div className="flex align-items-center justify-content-end gap-1 flex-nowrap">
                                                    {savedAudioIds.has(rowData.audioId) ? (
                                                        <Button
                                                            icon="pi pi-play"
                                                            className="p-button-secondary pr-0"
                                                            loading={
                                                                ivrAudioLoading ===
                                                                `menu-${rowData.audioId}`
                                                            }
                                                            onClick={() =>
                                                                handlePlayIvrAudio(
                                                                    rowData.audioId,
                                                                    rowData.audioName,
                                                                    `menu-${rowData.audioId}`
                                                                )
                                                            }
                                                            disabled={detailsLoading}
                                                        />
                                                    ) : (
                                                        <Button
                                                            icon={
                                                                hasAudioUpdatePermission
                                                                    ? "pi pi-pencil"
                                                                    : "pi pi-eye"
                                                            }
                                                            className="p-button-secondary pr-0"
                                                            loading={
                                                                detailsLoading &&
                                                                audioFieldLoadingKey ===
                                                                    `start-menu-${rowData.audioId}`
                                                            }
                                                            onClick={() => {
                                                                dispatch(
                                                                    getAudioById(
                                                                        rowData.groupId,
                                                                        jwtToken?.jwtToken,
                                                                        true
                                                                    )
                                                                );
                                                                setAudioFieldLoadingKey(
                                                                    `start-menu-${rowData.audioId}`
                                                                );
                                                                dispatch(
                                                                    setEditType(
                                                                        !!hasAudioUpdatePermission
                                                                    )
                                                                );
                                                            }}
                                                            disabled={detailsLoading}
                                                        />
                                                    )}
                                                    <Button
                                                        icon="pi pi-times"
                                                        className="p-button-secondary px-0"
                                                        onClick={() => {
                                                            if (savedAudioIds.has(rowData.audioId)) {
                                                                void (async () => {
                                                                    const ok =
                                                                        await removeIvrAudioFromSavedElement(
                                                                            rowData.audioId
                                                                        );
                                                                    if (!ok) return;
                                                                    setMenuAudioIds(
                                                                        (prev) =>
                                                                            prev.filter(
                                                                                (id) =>
                                                                                    id !==
                                                                                    rowData.audioId
                                                                            )
                                                                    );
                                                                })();
                                                            } else {
                                                                setMenuAudioIds(
                                                                    (prev) =>
                                                                        prev.filter(
                                                                            (id) =>
                                                                                id !==
                                                                                rowData.audioId
                                                                        )
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </DataTable>
                                    <AudioDialog />
                                </div>
                            )}

                            <Divider/>

                            <div className="mb-3">
                                <h5>Silens</h5>
                                <div className="p-fluid grid">
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Silens Pre
                                        </label>
                                        <InputNumber
                                            value={menuSilensPre}
                                            onValueChange={(e) =>
                                                setMenuSilensPre(e.value ?? null)
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Silens Post
                                        </label>
                                        <InputNumber
                                            value={menuSilensPost}
                                            onValueChange={(e) =>
                                                setMenuSilensPost(e.value ?? null)
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Divider/>

                            <div className="mb-3">
                                <div className="field col-12 md:col-6 p-0"><h5 className="mb-0">Timeout</h5></div>
                                <div className="p-fluid grid">
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Timeout
                                        </label>
                                        <InputNumber
                                            value={menuTimeout}
                                            onValueChange={(e) =>
                                                setMenuTimeout(e.value ?? null)
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label className="block font-semibold mb-2">
                                            Retries
                                        </label>
                                        <InputNumber
                                            value={menuTimeoutRetries}
                                            onValueChange={(e) =>
                                                setMenuTimeoutRetries(
                                                    e.value ?? null
                                                )
                                            }
                                            useGrouping={false}
                                            className="w-full"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>

                            <Divider/>

                            <div className="mb-3">
                                <h5 className="mb-2">DTMF</h5>
                                <p className="text-color-secondary text-sm mb-2">
                                    Оберіть клавіші для меню (натисніть для виділення/зняття)
                                </p>
                                <div
                                    className="ivr-dtmf-keypad p-3 border-round"
                                    style={ivrDtmfKeypadContainerStyle}
                                >
                                    {DTMF_KEYPAD_KEYS.map((key) => {
                                        const selected = menuDtmfKeys.includes(key);
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                className="p-2 border-round border-1"
                                                style={ivrDtmfKeyButtonStyle(selected)}
                                                onClick={() => {
                                                    setMenuDtmfKeys((prev) => {
                                                        const next = selected
                                                            ? prev.filter((k) => k !== key)
                                                            : [...prev, key];
                                                        return sortIvrMenuDtmfKeys(next);
                                                    });
                                                    setMenuDtmfRetries((prev) => {
                                                        if (selected) {
                                                            const { [key]: _, ...rest } = prev;
                                                            return rest;
                                                        }
                                                        return {
                                                            ...prev,
                                                            [key]: prev[key] ?? null,
                                                        };
                                                    });
                                                }}
                                            >
                                                {key}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {menuDtmfKeys.length > 0 && (
                                <div className="mb-3">
                                    <h5 className="mb-2">
                                        Retries для кожної DTMF кнопки
                                    </h5>
                                    <div className="p-fluid grid">
                                        {menuDtmfKeys.map((key) => (
                                            <div
                                                key={key}
                                                className="field col-12 md:col-4"
                                            >
                                                <label className="block font-semibold mb-2">
                                                 {key}
                                                </label>
                                                <InputNumber
                                                    value={
                                                        menuDtmfRetries[key] ??
                                                        null
                                                    }
                                                    onValueChange={(e) =>
                                                        setMenuDtmfRetries(
                                                            (prev) => ({
                                                                ...prev,
                                                                [key]:
                                                                    e.value ??
                                                                    null,
                                                            })
                                                        )
                                                    }
                                                    useGrouping={false}
                                                    className="w-full"
                                                    placeholder="0"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mb-3">
                                <h5>Retries (Others)</h5>
                                <InputNumber
                                    value={menuRetries}
                                    onValueChange={(e) =>
                                        setMenuRetries(e.value ?? null)
                                    }
                                    useGrouping={false}
                                    className="w-full"
                                    placeholder="0"
                                />
                            </div>
                        </>
                    )}

                    {startConfiguration === "Scheduler" && (
                        <div className="p-fluid">
                            <div className="field mb-4">
                                <label className="block font-semibold mb-2">
                                    Тип розкладу
                                </label>
                                <Dropdown
                                    value={schedulerScheduleKind}
                                    options={SCHEDULER_SCHEDULE_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) =>
                                        handleSchedulerScheduleKindChange(
                                            e.value
                                        )
                                    }
                                    className="w-full"
                                    placeholder="Оберіть тип"
                                />
                            </div>

                            {schedulerScheduleKind === "daysweek" && (
                                <>
                                    <h5 className="mt-0">Дні тижня</h5>
                                    <div className="grid">
                                        {schedulerDays.map((day) => (
                                            <div key={day.dayOfWeek} className="col-12">
                                                <div className="grid align-items-center">
                                                    <div className="col-6 md:col-2">
                                                        <div className="p-inputtext w-full opacity-100 text-center">
                                                            {WEEKDAY_ABBREVS[day.dayOfWeek] ??
                                                                day.label}
                                                        </div>
                                                    </div>
                                                    <div className="col-2 md:col-1 flex justify-content-center">
                                                        <Checkbox
                                                            checked={day.enabled}
                                                            onChange={(e) => {
                                                                const checked = !!e.checked;
                                                                setSchedulerDays((prev) =>
                                                                    prev.map((d) =>
                                                                        d.dayOfWeek ===
                                                                        day.dayOfWeek
                                                                            ? {
                                                                                  ...d,
                                                                                  enabled:
                                                                                      checked,
                                                                                  start: checked
                                                                                      ? defaultWeekdayScheduleStart()
                                                                                      : null,
                                                                                  end: checked
                                                                                      ? defaultWeekdayScheduleEnd()
                                                                                      : null,
                                                                              }
                                                                            : d
                                                                    )
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-4 md:col-4 flex align-items-center">
                                                        <div className="mr-2">З</div>
                                                        <Calendar
                                                            value={day.start}
                                                            onChange={(e) => {
                                                                const v = e.value as Date | null;
                                                                if (!v) return;
                                                                setSchedulerDays((prev) =>
                                                                    prev.map((d) =>
                                                                        d.dayOfWeek ===
                                                                        day.dayOfWeek
                                                                            ? { ...d, start: v }
                                                                            : d
                                                                    )
                                                                );
                                                            }}
                                                            timeOnly
                                                            hourFormat="24"
                                                            disabled={!day.enabled}
                                                            className="opacity-100"
                                                            placeholder="00:00"
                                                        />
                                                    </div>
                                                    <div className="col-4 md:col-4 flex align-items-center">
                                                        <div className="mr-2">По</div>
                                                        <Calendar
                                                            value={day.end}
                                                            onChange={(e) => {
                                                                const v = e.value as Date | null;
                                                                if (!v) return;
                                                                setSchedulerDays((prev) =>
                                                                    prev.map((d) =>
                                                                        d.dayOfWeek ===
                                                                        day.dayOfWeek
                                                                            ? { ...d, end: v }
                                                                            : d
                                                                    )
                                                                );
                                                            }}
                                                            timeOnly
                                                            hourFormat="24"
                                                            disabled={!day.enabled}
                                                            className="opacity-100"
                                                            placeholder="23:59"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {schedulerScheduleKind === "daysweek_advanced" && (
                                <>
                                    <h5 className="mt-0">Дні тижня</h5>
                                    {schedulerDaysweekAdvancedDays.map((day) => (
                                        <div
                                            key={day.dayOfWeek}
                                            className="col-12 mb-3 border-1 surface-border border-round p-2"
                                        >
                                            <div className="grid align-items-center mb-2">
                                                <div className="col-6 md:col-2">
                                                    <div className="p-inputtext w-full opacity-100 text-center">
                                                        {WEEKDAY_ABBREVS[day.dayOfWeek] ??
                                                            day.label}
                                                    </div>
                                                </div>
                                                <div className="col-2 md:col-1 flex justify-content-center">
                                                    <Checkbox
                                                        checked={day.enabled}
                                                        onChange={(e) => {
                                                            const checked = !!e.checked;
                                                            setSchedulerDaysweekAdvancedDays(
                                                                (prev) =>
                                                                    prev.map((d) =>
                                                                        d.dayOfWeek ===
                                                                        day.dayOfWeek
                                                                            ? {
                                                                                  ...d,
                                                                                  enabled:
                                                                                      checked,
                                                                                  intervals:
                                                                                      checked &&
                                                                                      d.intervals
                                                                                          .length ===
                                                                                          0
                                                                                          ? [
                                                                                                {
                                                                                                    priority: 1,
                                                                                                    start: defaultWeekdayScheduleStart(),
                                                                                                    end: defaultWeekdayScheduleEnd(),
                                                                                                },
                                                                                            ]
                                                                                          : d.intervals,
                                                                              }
                                                                            : d
                                                                    )
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {day.enabled &&
                                                day.intervals.map((row, idx) => {
                                                    const invalidKey = `${day.dayOfWeek}-${idx}`;
                                                    const rowInvalid =
                                                        schedulerDaysweekAdvancedInvalidKeys.has(
                                                            invalidKey
                                                        );
                                                    return (
                                                        <div
                                                            key={`${day.dayOfWeek}-${row.id ?? "new"}-${idx}`}
                                                            className={classNames(
                                                                "grid align-items-end mb-2 p-2 border-round",
                                                                {
                                                                    "surface-100 border-1 border-red-500":
                                                                        rowInvalid,
                                                                }
                                                            )}
                                                        >
                                                            <div className="col-12 md:col-5 flex align-items-center">
                                                                <label
                                                                    className={classNames(
                                                                        "mr-2 mb-0 white-space-nowrap",
                                                                        {
                                                                            "p-error":
                                                                                rowInvalid,
                                                                        }
                                                                    )}
                                                                >
                                                                    З
                                                                    <span className="p-error">
                                                                        *
                                                                    </span>
                                                                </label>
                                                                <Calendar
                                                                    value={row.start}
                                                                    onChange={(e) => {
                                                                        const v =
                                                                            e.value as Date | null;
                                                                        if (!v) {
                                                                            return;
                                                                        }
                                                                        setSchedulerDaysweekAdvancedDays(
                                                                            (prev) =>
                                                                                prev.map(
                                                                                    (d) =>
                                                                                        d.dayOfWeek ===
                                                                                        day.dayOfWeek
                                                                                            ? {
                                                                                                  ...d,
                                                                                                  intervals:
                                                                                                      d.intervals.map(
                                                                                                          (
                                                                                                              x,
                                                                                                              i
                                                                                                          ) =>
                                                                                                              i ===
                                                                                                              idx
                                                                                                                  ? {
                                                                                                                        ...x,
                                                                                                                        start: v,
                                                                                                                    }
                                                                                                                  : x
                                                                                                      ),
                                                                                              }
                                                                                            : d
                                                                                )
                                                                        );
                                                                    }}
                                                                    timeOnly
                                                                    hourFormat="24"
                                                                    disabled={
                                                                        !day.enabled
                                                                    }
                                                                    className={classNames(
                                                                        "opacity-100 w-full",
                                                                        {
                                                                            "p-invalid":
                                                                                rowInvalid,
                                                                        }
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="col-12 md:col-5 flex align-items-center">
                                                                <label
                                                                    className={classNames(
                                                                        "mr-2 mb-0 white-space-nowrap",
                                                                        {
                                                                            "p-error":
                                                                                rowInvalid,
                                                                        }
                                                                    )}
                                                                >
                                                                    По
                                                                    <span className="p-error">
                                                                        *
                                                                    </span>
                                                                </label>
                                                                <Calendar
                                                                    value={row.end}
                                                                    onChange={(e) => {
                                                                        const v =
                                                                            e.value as Date | null;
                                                                        if (!v) {
                                                                            return;
                                                                        }
                                                                        setSchedulerDaysweekAdvancedDays(
                                                                            (prev) =>
                                                                                prev.map(
                                                                                    (d) =>
                                                                                        d.dayOfWeek ===
                                                                                        day.dayOfWeek
                                                                                            ? {
                                                                                                  ...d,
                                                                                                  intervals:
                                                                                                      d.intervals.map(
                                                                                                          (
                                                                                                              x,
                                                                                                              i
                                                                                                          ) =>
                                                                                                              i ===
                                                                                                              idx
                                                                                                                  ? {
                                                                                                                        ...x,
                                                                                                                        end: v,
                                                                                                                    }
                                                                                                                  : x
                                                                                                      ),
                                                                                              }
                                                                                            : d
                                                                                )
                                                                        );
                                                                    }}
                                                                    timeOnly
                                                                    hourFormat="24"
                                                                    disabled={
                                                                        !day.enabled
                                                                    }
                                                                    className={classNames(
                                                                        "opacity-100 w-full",
                                                                        {
                                                                            "p-invalid":
                                                                                rowInvalid,
                                                                        }
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="col-12 md:col-2 flex justify-content-end">
                                                                <Button
                                                                    type="button"
                                                                    icon="pi pi-trash"
                                                                    className="p-button-text p-button-secondary"
                                                                    disabled={
                                                                        day.intervals
                                                                            .length <= 1
                                                                    }
                                                                    onClick={() =>
                                                                        setSchedulerDaysweekAdvancedDays(
                                                                            (prev) =>
                                                                                prev.map(
                                                                                    (d) =>
                                                                                        d.dayOfWeek ===
                                                                                        day.dayOfWeek
                                                                                            ? {
                                                                                                  ...d,
                                                                                                  intervals:
                                                                                                      d.intervals.filter(
                                                                                                          (
                                                                                                              _,
                                                                                                              i
                                                                                                          ) =>
                                                                                                              i !==
                                                                                                              idx
                                                                                                      ),
                                                                                              }
                                                                                            : d
                                                                                )
                                                                        )
                                                                    }
                                                                    aria-label="Видалити інтервал"
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            {day.enabled && (
                                                <Button
                                                    type="button"
                                                    label="Додати"
                                                    icon="pi pi-plus"
                                                    className="p-button-sm"
                                                    onClick={() =>
                                                        setSchedulerDaysweekAdvancedDays(
                                                            (prev) =>
                                                                prev.map((d) => {
                                                                    if (
                                                                        d.dayOfWeek !==
                                                                        day.dayOfWeek
                                                                    ) {
                                                                        return d;
                                                                    }
                                                                    const nextP =
                                                                        d.intervals.reduce(
                                                                            (m, x) =>
                                                                                Math.max(
                                                                                    m,
                                                                                    Number(
                                                                                        x.priority
                                                                                    ) || 0
                                                                                ),
                                                                            0
                                                                        ) + 1;
                                                                    const last =
                                                                        d.intervals[
                                                                            d.intervals
                                                                                .length - 1
                                                                        ];
                                                                    const start =
                                                                        last?.end !=
                                                                        null
                                                                            ? cloneTimeHmFromDate(
                                                                                  last.end
                                                                              )
                                                                            : defaultWeekdayScheduleStart();
                                                                    const end =
                                                                        defaultWeekdayScheduleEnd();
                                                                    return {
                                                                        ...d,
                                                                        intervals: [
                                                                            ...d.intervals,
                                                                            {
                                                                                priority: nextP,
                                                                                start,
                                                                                end,
                                                                            },
                                                                        ],
                                                                    };
                                                                })
                                                        )
                                                    }
                                                />
                                            )}
                                        </div>
                                    ))}
                                    {ivrBlockErrorFields?.schedulerSlots && (
                                        <small className="p-error block mt-2">
                                            {ivrBlockFieldErrorMessage(
                                                ivrBlockErrorFields.schedulerSlots
                                            )}
                                        </small>
                                    )}
                                </>
                            )}

                            {schedulerScheduleKind === "once" && (
                                <>
                                    <h5 className="mt-0 mb-2">Щоденний інтервал</h5>
                                    <div className="grid align-items-center mb-2">
                                        <div className="col-12 md:col-6 flex align-items-center">
                                            <label className="mr-2 mb-0 white-space-nowrap cursor-default">
                                                З<span className="p-error">*</span>
                                            </label>
                                            <Calendar
                                                value={schedulerExtraStart}
                                                onChange={(e) => {
                                                    const v = e.value as Date | null;
                                                    setSchedulerExtraStart(v);
                                                }}
                                                timeOnly
                                                hourFormat="24"
                                                className="opacity-100 w-full"
                                                placeholder="00:00"
                                            />
                                        </div>
                                        <div className="col-12 md:col-6 flex align-items-center">
                                            <label className="mr-2 mb-0 white-space-nowrap cursor-default">
                                                По<span className="p-error">*</span>
                                            </label>
                                            <Calendar
                                                value={schedulerExtraEnd}
                                                onChange={(e) => {
                                                    const v = e.value as Date | null;
                                                    setSchedulerExtraEnd(v);
                                                }}
                                                timeOnly
                                                hourFormat="24"
                                                className="opacity-100 w-full"
                                                placeholder="23:59"
                                            />
                                        </div>
                                    </div>
                                    {schedulerDailyComplement && (
                                        <div className="grid align-items-center">
                                            <div className="col-12 md:col-6 flex align-items-center">
                                                <label className="mr-2 mb-0 white-space-nowrap cursor-default">
                                                    З<span className="p-error">*</span>
                                                </label>
                                                <Calendar
                                                    value={schedulerDailyComplement.start2}
                                                    disabled
                                                    timeOnly
                                                    hourFormat="24"
                                                    className="opacity-100 w-full"
                                                />
                                            </div>
                                            <div className="col-12 md:col-6 flex align-items-center">
                                                <label className="mr-2 mb-0 white-space-nowrap cursor-default">
                                                    По<span className="p-error">*</span>
                                                </label>
                                                <Calendar
                                                    value={schedulerDailyComplement.end2}
                                                    disabled
                                                    timeOnly
                                                    hourFormat="24"
                                                    className="opacity-100 w-full"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {schedulerScheduleKind === "once_advanced" && (
                                <>
                                    <h5 className="mt-0 mb-2">
                                        Once (advanced)
                                    </h5>
                                    {schedulerAdvancedRows.map((row, idx) => {
                                        const rowInvalid =
                                            schedulerAdvancedInvalidRows.has(
                                                idx
                                            );
                                        return (
                                        <div
                                            key={`${row.id ?? "new"}-${idx}`}
                                            className={classNames(
                                                "grid align-items-end mb-3 p-2 border-round",
                                                {
                                                    "surface-100 border-1 border-red-500":
                                                        rowInvalid,
                                                }
                                            )}
                                        >
                                            <div className="col-12 md:col-5 flex align-items-center">
                                                <label
                                                    className={classNames(
                                                        "mr-2 mb-0 white-space-nowrap cursor-default",
                                                        { "p-error": rowInvalid }
                                                    )}
                                                >
                                                    З<span className="p-error">*</span>
                                                </label>
                                                <Calendar
                                                    value={row.start}
                                                    onChange={(e) => {
                                                        const v = e.value as Date | null;
                                                        if (!v) return;
                                                        setSchedulerAdvancedRows((prev) =>
                                                            prev.map((x, i) =>
                                                                i === idx
                                                                    ? { ...x, start: v }
                                                                    : x
                                                            )
                                                        );
                                                    }}
                                                    timeOnly
                                                    hourFormat="24"
                                                    className={classNames(
                                                        "opacity-100 w-full",
                                                        { "p-invalid": rowInvalid }
                                                    )}
                                                    placeholder="00:00"
                                                />
                                            </div>
                                            <div className="col-12 md:col-5 flex align-items-center">
                                                <label
                                                    className={classNames(
                                                        "mr-2 mb-0 white-space-nowrap cursor-default",
                                                        { "p-error": rowInvalid }
                                                    )}
                                                >
                                                    По<span className="p-error">*</span>
                                                </label>
                                                <Calendar
                                                    value={row.end}
                                                    onChange={(e) => {
                                                        const v = e.value as Date | null;
                                                        if (!v) return;
                                                        setSchedulerAdvancedRows((prev) =>
                                                            prev.map((x, i) =>
                                                                i === idx ? { ...x, end: v } : x
                                                            )
                                                        );
                                                    }}
                                                    timeOnly
                                                    hourFormat="24"
                                                    className={classNames(
                                                        "opacity-100 w-full",
                                                        { "p-invalid": rowInvalid }
                                                    )}
                                                    placeholder="23:59"
                                                />
                                            </div>
                                            <div className="col-12 md:col-2 flex justify-content-end">
                                                <Button
                                                    type="button"
                                                    icon="pi pi-trash"
                                                    className="p-button-text p-button-secondary"
                                                    disabled={
                                                        schedulerAdvancedRows.length <= 1
                                                    }
                                                    onClick={() =>
                                                        setSchedulerAdvancedRows((prev) =>
                                                            prev.filter((_, i) => i !== idx)
                                                        )
                                                    }
                                                    aria-label="Видалити інтервал"
                                                />
                                            </div>
                                        </div>
                                        );
                                    })}
                                    <Button
                                        type="button"
                                        label="Додати"
                                        icon="pi pi-plus"
                                        className="p-button-sm mb-2"
                                        onClick={() =>
                                            setSchedulerAdvancedRows((prev) => {
                                                const nextP =
                                                    prev.reduce(
                                                        (m, x) =>
                                                            Math.max(
                                                                m,
                                                                Number(x.priority) || 0
                                                            ),
                                                        0
                                                    ) + 1;
                                                const last = prev[prev.length - 1];
                                                const start =
                                                    last?.end != null
                                                        ? cloneTimeHmFromDate(last.end)
                                                        : defaultStartTime();
                                                const end = defaultEndTime();
                                                return [
                                                    ...prev,
                                                    {
                                                        priority: nextP,
                                                        start,
                                                        end,
                                                    },
                                                ];
                                            })
                                        }
                                    />
                                    {ivrBlockErrorFields?.schedulerSlots && (
                                        <small className="p-error block mt-2">
                                            {ivrBlockFieldErrorMessage(
                                                ivrBlockErrorFields.schedulerSlots
                                            )}
                                        </small>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {startConfiguration === "AgentHunter" && (
                        <div className="mb-3" ref={hunterQueueFieldRef}>
                            <h5 className="mt-0 mb-2">Agent Hunter</h5>
                            <label
                                className={classNames("block font-semibold mb-2", {
                                    "p-error": ivrBlockErrorFields?.hunterQueue,
                                })}
                            >
                                Черга<span className="p-error"></span>
                            </label>
                            <Dropdown
                                filter
                                value={hunterQueueIds[0] ?? null}
                                onChange={(e) =>
                                    setHunterQueueIds(e.value != null ? [e.value] : [])
                                }
                                options={(allQueues ?? []).map((q: any) => ({
                                    ...q,
                                    idName: `ID: ${q.id} "${q.name}"`,
                                }))}
                                optionLabel="idName"
                                optionValue="id"
                                placeholder="Виберіть чергу"
                                className={classNames("w-full", {
                                    "p-invalid": ivrBlockErrorFields?.hunterQueue,
                                })}
                            />
                            {ivrBlockErrorFields?.hunterQueue && (
                                <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                                    <span className="title p-error text-sm">
                                        {ivrBlockFieldErrorMessage(
                                            ivrBlockErrorFields.hunterQueue
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {startConfiguration !== "End" &&<Divider/>}

            {isEvent && !isStartBase && !isStartConfig && (
                <div className="mb-4">
                    <label className="block font-semibold mb-2">Лінія</label>
                    <MultiSelect
                        filter
                        value={asMultiSelectArray(selectedLines)}
                        onChange={(e) =>
                            setSelectedLines(
                                asMultiSelectArray(
                                    e.value as
                                        | { id: string; name: string }
                                        | { id: string; name: string }[]
                                        | null
                                        | undefined
                                )
                            )
                        }
                        options={MOCK_LINES}
                        optionLabel="name"
                        placeholder="Вибір лінії"
                        className="w-full"
                        display="chip"
                        dataKey="id"
                    />
                </div>
            )}

            {isGateway && (
                <>
                    <div className="mb-4">
                        <label className="block font-semibold mb-2">
                            Тип даних
                        </label>
                        <Dropdown
                            filter
                            value={dataType}
                            onChange={(e) => setDataType(e.value ?? "")}
                            options={DATA_TYPE_OPTIONS}
                            optionLabel="label"
                            optionValue="id"
                            placeholder="Оберіть тип даних"
                            className="w-full"
                        />
                    </div>
                    {dataType === "day_of_week" && (
                        <div className="mb-4">
                            <label className="block font-semibold mb-2">
                                Дні тижня
                            </label>
                            <Dropdown
                                filter
                                value={selectedDay}
                                onChange={(e) =>
                                    setSelectedDay(e.value ?? "")
                                }
                                options={DAY_OPTIONS}
                                optionLabel="label"
                                optionValue="id"
                                placeholder="Оберіть день"
                                className="w-full"
                            />
                        </div>
                    )}
                    {dataType === "number" && (
                        <div className="mb-4">
                            <h6 className="font-semibold mb-2">Умова</h6>
                            <SelectButton
                                value={conditionMode}
                                onChange={(e) =>
                                    setConditionMode(e.value ?? "comparison")
                                }
                                options={CONDITION_MODE_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                className="w-full mb-3"
                            />
                            {conditionMode === "comparison" && (
                                <>
                                    <div className="flex flex-column gap-2 mb-3">
                                        {COMPARISON_OPERATORS.map((opt) => (
                                            <div
                                                key={opt.id}
                                                className="flex align-items-center"
                                            >
                                                <RadioButton
                                                    inputId={`op-${opt.id}`}
                                                    name="comparisonOp"
                                                    value={opt.id}
                                                    onChange={(e) =>
                                                        setComparisonOperator(
                                                            e.value ?? "="
                                                        )
                                                    }
                                                    checked={
                                                        comparisonOperator ===
                                                        opt.id
                                                    }
                                                />
                                                <label
                                                    htmlFor={`op-${opt.id}`}
                                                    className="ml-2 cursor-pointer"
                                                >
                                                    {opt.id} {opt.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <label className="block font-semibold mb-2">
                                        Значення умови
                                    </label>
                                    <InputNumber
                                        value={conditionValue}
                                        onValueChange={(e) =>
                                            setConditionValue(e.value ?? null)
                                        }
                                        className="w-full"
                                        placeholder="Значення"
                                    />
                                </>
                            )}
                            {conditionMode === "range" && (
                                <div className="text-color-secondary text-sm">
                                    Діапазон (в розробці)
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {data.shape === "triangle" && (
                <div className="mb-4 text-color-secondary">
                    Налаштування блоку дії (в розробці)
                </div>
            )}

            {data.shape === "square" && (
                <div className="mb-4 text-color-secondary">
                    Налаштування блоку (в розробці)
                </div>
            )}

            {!isStartBase && (
                <Button
                    label="Зберегти"
                    icon="pi pi-check"
                    onClick={() => void handleSave()}
                    className="w-full"
                    loading={savingIvrElement}
                    disabled={savingIvrElement || !canMutateIVRScheme}
                />
            )}
            </fieldset>

            <Dialog
                header={ivrAudioDialogName || "Програвач"}
                visible={ivrAudioDialogVisible}
                style={{ width: "25vw" }}
                className="pb-0 audio-dialog"
                onHide={handleCloseIvrAudioDialog}
            >
                {ivrAudioDialogUrl && (
                    <div className="field col-12 md:col-12 flex flex-column align-items-center relative mt-2">
                        <div className="w-full relative">
                            <AudioPlayerComponent
                                ref={ivrAudioPlayerRef}
                                className="audio-player border-round-sm mt-3"
                                autoPlay={false}
                                src={ivrAudioDialogUrl}
                                style={{
                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                }}
                            />
                            <Button
                                icon="pi pi-download"
                                onClick={handleDownloadIvrAudio}
                                style={{ marginLeft: "10px" }}
                                className="absolute outline-none border-none shadow-none border-circle save-mp3 text-lg opacity-100"
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default BlockSettingsPanel;
